import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// ============ CONSTANTS ============

// Swedish category translations for suppliers
export const SUPPLIER_CATEGORIES: Record<string, string> = {
  plumbing: 'VVS/Rörmokare',
  electrical: 'Elektriker',
  heating: 'Värme',
  ventilation: 'Ventilation',
  locksmith: 'Låssmed',
  elevator: 'Hiss',
  cleaning: 'Städ',
  garden: 'Trädgård/Mark',
  construction: 'Bygg/Snickeri',
  glass: 'Glas/Fönster',
  security: 'Larm/Säkerhet',
  waste: 'Avfall/Sophantering',
  pest: 'Skadedjur',
  other: 'Övrigt'
};

// ============ TYPE DEFINITIONS ============

// Base entity types
export interface BaseEntity {
  id: string;
  created_at?: string;
  updated_at?: string;
}

export interface User extends BaseEntity {
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role?: 'superadmin' | 'admin' | 'auditor' | 'suppliant' | 'board_member' | 'resident' | 'supplier' | 'external';
  organization_id?: string;
  apartment_id?: string;
  property_id?: string;
  personal_number?: string;
  is_active?: boolean;
  is_invoice_recipient?: boolean;
  move_in_date?: string;
  move_out_date?: string;
  previous_apartments?: string[];
}

export interface Organization extends BaseEntity {
  name?: string;
  status?: 'active' | 'inactive' | 'pending';
  subscription_type?: string;
  subscription_plan?: string;
  subscription_status?: string;
  org_number?: string;
  contact_email?: string;
  contact_phone?: string;
  slug?: string; // URL-friendly name for portal
  subdomain_code?: string; // Unique 8-char code for subdomain (e.g., a7b3x9k2.forvaltningen.se)
  portal_settings?: {
    header_image?: string;
    background_color?: string;
    description?: string;
    logo?: string;
    is_public?: boolean;
    custom_domain?: string; // Custom domain for member update links (e.g., https://medlem.grindstugatan.se)
  };
}

export interface Announcement extends BaseEntity {
  title?: string;
  content?: string;
  category?: 'news' | 'reminder' | 'important' | 'event';
  organization_id?: string;
  created_by?: string;
  created_by_name?: string;
  is_published?: boolean;
  is_public?: boolean; // Visible on public portal
  publish_date?: string;
  end_date?: string; // When the announcement expires/disappears
  image_url?: string; // Main image for the announcement (legacy/thumbnail)
  images?: string[]; // Array of image URLs for gallery
  attachments?: string[]; // Array of non-image file URLs
}

export interface Property extends BaseEntity {
  name?: string;
  address?: string;
  property_designation?: string;
  organization_id?: string;
  description?: string;
}

export interface Apartment extends BaseEntity {
  apartment_number?: string;
  property_id?: string;
  organization_id?: string;
  floor?: number;
  area?: number;
  area_sqm?: number;
  rooms?: number;
  type?: string;
  dwelling_type?: string;
  residents?: User[];
  resident_count?: number;
  storage_units?: string[];
}

export interface Issue extends BaseEntity {
  title?: string;
  description?: string;
  category?: string;
  urgency_level?: number;
  status?: 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
  reporter_email?: string;
  reporter_name?: string;
  reporter_phone?: string;
  property_id?: string;
  apartment_id?: string;
  organization_id?: string;
  assigned_type?: 'internal' | 'supplier';
  supplier_id?: string;
  resolution?: string;
  resolution_notes?: string;
  source?: string;
  issue_number?: string;
  email_thread_id?: string;
  internal_notes?: Array<{id: string; text: string; author_name?: string; created_at: string}>;
  attachments?: string[];
}

export interface Order extends BaseEntity {
  issue_id?: string;
  subcontractor_name?: string;
  subcontractor_email?: string;
  subcontractor_phone?: string;
  instructions?: string;
  instructions_html?: string;
  status?: 'pending' | 'accepted' | 'rejected' | 'confirmed' | 'waiting_quote_approval' | 'waiting_invoice' | 'completed';
  due_date?: string;
  confirmation_token?: string;
  access_token?: string;
  response?: string;
  responded_at?: string;
  confirmed_at?: string;
  is_internal?: boolean;
  quote_amount?: number;
  quote_description?: string;
  quote_attachments?: string[];
  completion_notes?: string;
  completion_attachments?: string[];
  completed_at?: string;
  supplier_messages?: Array<{id: string; text: string; created_at: string; from?: string}>;
}

export interface Supplier extends BaseEntity {
  name?: string;
  email?: string;
  phone?: string;
  category?: string;
  is_active?: boolean;
  organization_id?: string;
}

export interface Document extends BaseEntity {
  name?: string;
  description?: string;
  category?: string;
  custom_category?: string; // For user-defined categories
  file_url?: string;
  file_type?: string;
  file_size?: number;
  organization_id?: string;
  assigned_to_user?: string;
  assigned_to_apartment?: string;
  assigned_to_property?: string;
  tags?: string[];
  visibility?: 'internal' | 'public'; // internal = only logged in, public = portal visible
}

export interface MaintenanceSchedule extends BaseEntity {
  title?: string;
  description?: string;
  category?: string;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'once';
  interval?: number;
  property_id?: string;
  property_name?: string;
  apartment_id?: string;
  organization_id?: string;
  start_date?: string;
  due_date?: string;
  next_date?: string | Date;
  days_until?: number;
  responsible?: string;
  estimated_duration?: string;
  notes?: string;
  reminder_enabled?: boolean;
  is_active?: boolean;
  last_completed?: string;
  last_completed_by?: string;
  last_completion_notes?: string;
  completion_count?: number;
  source_protocol_id?: string;
  estimated_cost?: number;
}

export interface InspectionProtocol extends BaseEntity {
  type?: 'annual' | 'move_in' | 'move_out' | 'damage' | 'moisture' | 'safety' | 'other';
  status?: 'draft' | 'completed';
  inspection_date?: string;
  property_id?: string;
  apartment_id?: string;
  organization_id?: string;
  inspector_name?: string;
  items?: InspectionItem[];
  notes?: string;
  finalized_at?: string;
  finalized_by?: string;
}

export interface InspectionItem {
  building_part?: string;
  building_part_name?: string;
  component_name?: string;
  status?: 'ok' | 'minor_defect' | 'action_required' | 'urgent';
  comment?: string;
  recommended_action?: string;
  estimated_cost?: number;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  images?: Array<{url: string; name?: string}>;
}

export interface ActionProtocol extends BaseEntity {
  title?: string;
  action_type?: string;
  action_date?: string;
  component_id?: string;
  property_id?: string;
  apartment_id?: string;
  organization_id?: string;
  cost?: number;
  performed_by?: string;
  performed_by_name?: string;
  new_status?: string;
  notes?: string;
}

export interface Component extends BaseEntity {
  name?: string;
  building_part?: string;
  property_id?: string;
  apartment_id?: string;
  organization_id?: string;
  status?: 'ok' | 'minor_defect' | 'action_required' | 'urgent';
  last_action_date?: string;
  installation_date?: string;
  warranty_expires?: string;
}

export interface ActivityLog extends BaseEntity {
  entity_type?: string;
  entity_id?: string;
  action?: string;
  details?: {
    description?: string;
    notes?: string;
    [key: string]: string | number | boolean | undefined;
  };
  user_id?: string;
  apartment_id?: string;
  property_id?: string;
  organization_id?: string;
  performed_by?: string;
  timestamp?: string;
}

export interface EmailThread extends BaseEntity {
  subject?: string;
  participant_email?: string;
  participant_name?: string;
  participants?: string[];
  issue_id?: string;
  messages?: EmailMessage[];
  last_message_at?: string;
  last_message_preview?: string;
  message_count?: number;
  is_read?: boolean;
  reference?: string;
}

export interface EmailMessage {
  id: string;
  direction: string;
  body?: string;
  from_email?: string;
  to_email?: string;
  created_at: string;
}

export interface ServiceMessage extends BaseEntity {
  title?: string;
  content?: string;
  message?: string;
  type?: string;
  organization_id?: string;
  push_sent?: number;
  email_sent?: number;
  sms_sent?: number;
}

export interface AuditLog extends BaseEntity {
  category?: string;
  action_category?: string;
  action?: string;
  action_description?: string;
  user_name?: string;
  user_email?: string;
  target?: string;
  target_name?: string;
  details?: Record<string, unknown> | null;
  timestamp?: string;
  created_at?: string;
  organization_name?: string;
  organization_id?: string;
}

export interface IncomeRecord extends BaseEntity {
  date: string;
  amount: number;
  category?: string;
  description?: string;
  apartment_id?: string;
  property_id?: string;
  organization_id?: string;
}

export interface ExpenseRecord extends BaseEntity {
  date: string;
  amount: number;
  category?: string;
  description?: string;
  supplier_id?: string;
  property_id?: string;
  organization_id?: string;
}

export interface EmergencyContact extends BaseEntity {
  name?: string;
  phone?: string;
  email?: string;
  category?: string;
  description?: string;
  organization_id?: string;
}

export interface ApiKey extends BaseEntity {
  key?: string;
  name?: string;
  permissions?: string[];
  organization_id?: string;
}

export interface Invitation extends BaseEntity {
  email?: string;
  role?: string;
  organization_id?: string;
  status?: 'pending' | 'accepted' | 'expired';
  expires_at?: string;
}

// Calendar Event for portal
export interface CalendarEvent extends BaseEntity {
  title?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  location?: string;
  organization_id?: string;
  is_public?: boolean;
}

// Gallery Image for portal
export interface GalleryImage extends BaseEntity {
  url?: string;
  thumbnail_url?: string;
  caption?: string;
  organization_id?: string;
  is_public?: boolean;
  sort_order?: number;
}

// API Response types
export interface ApiResponse<T> {
  data: T;
}

export interface BatchQueryResult {
  [collection: string]: unknown[];
}

export interface QueryOptions {
  limit?: number;
  skip?: number;
  sort?: Record<string, 1 | -1>;
  bypassCache?: boolean;
}

export interface DevSettings {
  dev_login_enabled?: boolean;
  [key: string]: unknown;
}

export interface DashboardStats {
  issues: {
    total: number;
    open: number;
    in_progress: number;
    resolved: number;
    emergency: number;
    by_category?: Record<string, number>;
    by_urgency?: Record<string, number>;
  };
  properties: number;
  apartments: number;
  users: number;
}

export interface AlertsData {
  active_issues: number;
  urgent_issues: number;
  unread_messages: number;
  urgent_messages: number;
}

export interface ProtocolStats {
  total_inspections: number;
  completed_inspections: number;
  draft_inspections: number;
  total_actions: number;
  total_cost: number;
  components_ok: number;
  components_minor: number;
  components_action: number;
  components_urgent: number;
}

// CommHub configuration
const COMMHUB_BASE = 'https://www.commhub.cloud/api';
export const COMMHUB_APP_ID = '3d6ef532-9aa9-467c-817b-627582e0972e';
const COMMHUB_API_KEY = process.env.REACT_APP_COMMHUB_API_KEY || 'GlHLVQpgdQaYcNoXxMoUKFTcUwXgZME_4RUZ6Z0AztA';

// ============ SIMPLE CACHE ============
interface CacheItem<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheItem<unknown>>();
const CACHE_TTL = 0; // DISABLED - Was 30 seconds, set to 0 to disable caching

const getCached = <T>(key: string): T | null => {
  const item = cache.get(key) as CacheItem<T> | undefined;
  if (item && Date.now() - item.timestamp < CACHE_TTL) {
    return item.data;
  }
  return null;
};

const setCache = <T>(key: string, data: T): void => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Clear cache for a collection (call after mutations)
export const invalidateCache = (collection: string): void => {
  for (const key of cache.keys()) {
    if (key.startsWith(collection)) {
      cache.delete(key);
    }
  }
};

// Clear all cache
export const clearCache = (): void => {
  cache.clear();
};

// Create axios instance for CommHub
const commhub: AxiosInstance = axios.create({
  baseURL: COMMHUB_BASE,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': COMMHUB_API_KEY,
  },
});

// Add auth token to CommHub requests (but NOT for public OTP endpoints)
commhub.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // These public endpoints should NOT have Authorization header (causes 500 errors)
  const isPublicOtpEndpoint = config.url?.includes('/public/') && 
    (config.url?.includes('/request-otp') || 
     config.url?.includes('/verify-otp') || 
     config.url?.includes('/check-totp'));
  
  if (isPublicOtpEndpoint) {
    // Remove any existing Authorization header for OTP endpoints
    if (config.headers) {
      delete config.headers.Authorization;
    }
    return config;
  }
  
  // For other requests (including TOTP setup which needs auth), add token if available
  const token = localStorage.getItem('token');
  if (token && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
commhub.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    // Log full error for debugging
    console.error('CommHub API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    if (error.response?.status === 401) {
      // Don't logout on 401 for public endpoints
      if (!error.config?.url?.includes('/public/')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// ============ COMMHUB USER TYPES ============

interface CommHubUserInternal {
  id: string;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role?: string;
  status?: 'active' | 'inactive' | 'pending' | 'suspended';
  organization_id?: string;
  apartment_id?: string;
  property_id?: string;
  tags?: string[];
  groups?: string[];
  is_active?: boolean;
  is_invoice_recipient?: boolean;
  move_in_date?: string;
  move_out_date?: string;
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
  personal_number?: string;
}

// Helper to convert CommHubUser to User (defined early for use in getUsers)
const commHubUserToUserInternal = (u: CommHubUserInternal): User => {
  const firstName = u.first_name || '';
  const lastName = u.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || u.name || '';
  
  return {
    id: u.id,
    name: fullName,
    email: u.email,
    phone: u.phone,
    role: (u.role || 'resident') as User['role'],
    organization_id: u.organization_id,
    apartment_id: u.apartment_id,
    property_id: u.property_id,
    is_active: u.is_active !== false,
    is_invoice_recipient: u.is_invoice_recipient || false,
    move_in_date: u.move_in_date,
    move_out_date: u.move_out_date
  };
};

// ============ HELPER FUNCTIONS ============

interface BatchQuery {
  collection: string;
  filter?: Record<string, unknown>;
  limit?: number;
}

// Batch query multiple collections in a single request
export const batchQuery = async (queries: BatchQuery[]): Promise<BatchQueryResult> => {
  const cacheKey = `batch:${JSON.stringify(queries)}`;
  
  const cached = getCached<BatchQueryResult>(cacheKey);
  if (cached) {
    return cached;
  }
  
  const response = await commhub.post('/data/batch/query', { queries });
  const results = response.data.results || {};
  
  const transformed: BatchQueryResult = {};
  for (const [collection, data] of Object.entries(results)) {
    const collectionData = data as { documents?: Array<{ id?: string; _id?: string; data?: unknown }> };
    const documents = collectionData.documents || [];
    // Include CommHub document ID for CRUD while preserving custom ID for relations
    transformed[collection] = documents.map((doc) => {
      const rawData = (doc.data || doc) as Record<string, unknown>;
      const commhubDocId = doc.id || doc._id;
      return {
        ...rawData,
        id: rawData.id || commhubDocId, // Preserve custom ID if exists
        _commhubId: commhubDocId // Always store CommHub ID for CRUD
      };
    });
  }
  
  setCache(cacheKey, transformed);
  
  return transformed;
};

// Query collection with optional filter (with caching)
export const queryCollection = async <T = unknown>(
  collection: string, 
  filter: Record<string, unknown> = {}, 
  options: QueryOptions = {}
): Promise<T[]> => {
  const { limit = 1000, skip = 0, sort, bypassCache = false } = options;
  
  const cacheKey = `${collection}:${JSON.stringify(filter)}:${limit}:${skip}`;
  
  if (!bypassCache) {
    const cached = getCached<T[]>(cacheKey);
    if (cached) {
      return cached;
    }
  }
  
  const response = await commhub.post(`/data/${collection}/query`, {
    filter,
    limit,
    skip,
    ...(sort && { sort })
  });
  
  const documents = response.data.documents || [];
  // Include the CommHub document ID for CRUD operations while preserving the custom data.id for relations
  const result = documents.map((doc: { id?: string; _id?: string; data?: T }) => {
    const rawData = (doc.data || doc) as T & { id?: string; _commhubId?: string };
    const commhubDocId = doc.id || doc._id;
    
    // Store CommHub's document ID as _commhubId for delete/update operations
    // Keep data.id (if exists) for relation lookups, otherwise use CommHub ID
    return {
      ...rawData,
      id: rawData.id || commhubDocId, // Preserve custom ID if exists, otherwise use CommHub ID
      _commhubId: commhubDocId // Always store CommHub ID for CRUD operations
    } as T;
  });
  
  setCache(cacheKey, result);
  
  return result;
};

// ============ AUTH (CommHub Public API) ============

export interface AuthData {
  token?: string;
  user?: User;
}

// Helper to normalize phone numbers for comparison
const normalizePhone = (phone: string): string => {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  // Remove leading country code (46 for Sweden)
  if (digits.startsWith('46')) {
    return digits.slice(2);
  }
  // Remove leading 0
  if (digits.startsWith('0')) {
    return digits.slice(1);
  }
  return digits;
};

// Helper to get user role from forvaltaren_users database
const enrichUserWithRole = async (authData: AuthData, identifier: string): Promise<AuthData> => {
  try {
    // Search in CommHub Users API (not legacy forvaltaren_users)
    const isEmail = identifier.includes('@');
    console.log('enrichUserWithRole - searching for:', identifier);
    
    // Get all CommHub users and find by email or phone
    const commHubUsers = await getCommHubUsersCached();
    
    let matchedUser;
    if (isEmail) {
      matchedUser = commHubUsers.find(u => 
        u.email?.toLowerCase() === identifier.toLowerCase()
      );
    } else {
      // Normalize phone numbers for comparison
      const normalizedIdentifier = normalizePhone(identifier);
      console.log('enrichUserWithRole - normalized phone:', normalizedIdentifier);
      matchedUser = commHubUsers.find(u => {
        if (!u.phone || !u.email) return false; // Skip users without email (ghost users from OTP)
        const normalizedUserPhone = normalizePhone(u.phone);
        return normalizedUserPhone === normalizedIdentifier;
      });
    }
    
    console.log('enrichUserWithRole - found user:', matchedUser ? matchedUser.email : 'none');
    
    if (matchedUser) {
      const enrichedUser: AuthData = {
        ...authData,
        user: {
          ...authData.user,
          id: matchedUser.id || authData.user?.id || '',
          email: matchedUser.email || authData.user?.email || '',
          name: `${matchedUser.first_name || ''} ${matchedUser.last_name || ''}`.trim() || matchedUser.name || authData.user?.name,
          role: (matchedUser.role || 'resident') as User['role'],
          organization_id: matchedUser.organization_id,
          is_active: matchedUser.is_active !== false
        }
      };
      console.log('enrichUserWithRole - enriched user role:', enrichedUser.user?.role);
      return enrichedUser;
    }
  } catch (error) {
    console.error('Error enriching user data:', error);
  }
  
  console.log('enrichUserWithRole - fallback, no user found in CommHub');
  return {
    ...authData,
    user: {
      ...authData.user,
      id: authData.user?.id || '',
      role: authData.user?.role || 'resident'
    }
  };
};

// Email OTP
export const requestLoginCode = (email: string): Promise<AxiosResponse> => 
  commhub.post(`/public/${COMMHUB_APP_ID}/request-otp`, { email });

export const verifyLoginCode = async (email: string, code: string): Promise<{ data: AuthData }> => {
  const response = await commhub.post(`/public/${COMMHUB_APP_ID}/verify-otp`, { email, code });
  const enrichedData = await enrichUserWithRole(response.data, email);
  return { data: enrichedData };
};

// SMS OTP
export const requestSmsOtp = async (phone: string): Promise<AxiosResponse> => {
  let formattedPhone = phone.replace(/[\s-]/g, '');
  if (formattedPhone.startsWith('0')) {
    formattedPhone = '+46' + formattedPhone.slice(1);
  } else if (!formattedPhone.startsWith('+')) {
    formattedPhone = '+46' + formattedPhone;
  }
  
  const response = await commhub.post(`/public/${COMMHUB_APP_ID}/request-otp`, {
    phone: formattedPhone,
    channel: 'sms'
  });
  return response;
};

export const verifySmsOtp = async (phone: string, code: string): Promise<{ data: AuthData }> => {
  let formattedPhone = phone.replace(/[\s-]/g, '');
  if (formattedPhone.startsWith('0')) {
    formattedPhone = '+46' + formattedPhone.slice(1);
  } else if (!formattedPhone.startsWith('+')) {
    formattedPhone = '+46' + formattedPhone;
  }
  
  const response = await commhub.post(`/public/${COMMHUB_APP_ID}/verify-otp`, {
    phone: formattedPhone,
    code: code
  });
  
  const enrichedData = await enrichUserWithRole(response.data, formattedPhone);
  return { data: enrichedData };
};

// TOTP (Authenticator App)
export const checkTotpEnabled = async (email: string): Promise<{ totp_enabled: boolean }> => {
  const response = await commhub.post(`/public/${COMMHUB_APP_ID}/check-totp?email=${encodeURIComponent(email)}`);
  return response.data;
};

export const loginWithTotp = async (email: string, code: string): Promise<{ data: AuthData }> => {
  // Use a separate axios instance to avoid interceptor interference
  const totpAxios = axios.create({
    baseURL: COMMHUB_BASE,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  
  const response = await totpAxios.post(`/public/${COMMHUB_APP_ID}/verify-totp`, {
    email: email,
    code: code
  });
  
  const enrichedData = await enrichUserWithRole(response.data, email);
  return { data: enrichedData };
};

// TOTP Setup
export const setupTotp = async (token: string): Promise<AxiosResponse> => {
  console.log('setupTotp - token length:', token?.length);
  console.log('setupTotp - token start:', token?.substring(0, 50));
  
  // Create a separate axios instance for TOTP to bypass interceptors
  const totpAxios = axios.create({
    baseURL: COMMHUB_BASE,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  
  const response = await totpAxios.post(`/public/${COMMHUB_APP_ID}/totp/setup`, {});
  return response;
};

export const verifyTotpSetup = async (token: string, code: string): Promise<AxiosResponse> => {
  // Create a separate axios instance for TOTP to bypass interceptors
  const totpAxios = axios.create({
    baseURL: COMMHUB_BASE,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  
  // Use /totp/verify endpoint (not /totp/verify-setup)
  const response = await totpAxios.post(`/public/${COMMHUB_APP_ID}/totp/verify`, { code });
  return response;
};

export const disableTotp = async (token: string): Promise<AxiosResponse> => {
  // Create a separate axios instance for TOTP to bypass interceptors
  const totpAxios = axios.create({
    baseURL: COMMHUB_BASE,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  
  const response = await totpAxios.post(`/public/${COMMHUB_APP_ID}/totp/disable`, {});
  return response;
};

export const getMe = (): Promise<AxiosResponse> => commhub.get(`/public/${COMMHUB_APP_ID}/me`);

export const logout = (): Promise<void> => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  clearCache();
  return Promise.resolve();
};

// ============ DATA HELPER FUNCTIONS ============

const getDocument = async <T>(collection: string, id: string): Promise<T | null> => {
  const docs = await queryCollection<T>(collection, { id }, { limit: 1 });
  return docs[0] || null;
};

export const createDocument = async <T extends Record<string, unknown>>(
  collection: string, 
  data: T
): Promise<T> => {
  const response = await commhub.post(`/data/${collection}`, { data });
  invalidateCache(collection);
  return response.data;
};

export const updateDocument = async <T>(
  collection: string, 
  id: string, 
  data: Partial<T> & Record<string, unknown>
): Promise<T> => {
  // First try to update directly using the ID (CommHub's internal _id)
  try {
    const directResponse = await commhub.patch(`/data/${collection}/${id}`, data);
    invalidateCache(collection);
    return directResponse.data;
  } catch (directError) {
    // Fall through to query approach
  }
  
  // Fallback: Try to find document by our custom 'id' field
  let response = await commhub.post(`/data/${collection}/query`, {
    filter: { id },
    limit: 1
  });
  
  // If not found by custom id, try by verification_token (for member_updates)
  if (!response.data.documents?.length && collection === 'member_updates') {
    response = await commhub.post(`/data/${collection}/query`, {
      filter: { verification_token: id },
      limit: 1
    });
  }
  
  if (!response.data.documents?.length) {
    console.error('Document not found with id:', id);
    throw new Error('Document not found');
  }
  
  const doc = response.data.documents[0];
  // CommHub uses _id internally, but returns it as id in the response
  const commhubId = doc._id || doc.id;
  console.log('Updating document:', commhubId, 'with data:', data);
  
  const updateResponse = await commhub.patch(`/data/${collection}/${commhubId}`, data);
  invalidateCache(collection);
  return updateResponse.data;
};

export const deleteDocument = async (collection: string, id: string): Promise<{ success: boolean }> => {
  // The ID passed here should be CommHub's document ID (from queryCollection)
  // Try direct delete first using CommHub's document ID
  try {
    await commhub.delete(`/data/${collection}/${id}`);
    invalidateCache(collection);
    return { success: true };
  } catch (directError) {
    // Fallback: If direct delete fails, try to find document by custom 'id' field in data
    console.log('Direct delete failed, trying query fallback for id:', id);
  }
  
  // Fallback: search for document with id in data object (legacy support)
  const response = await commhub.post(`/data/${collection}/query`, {
    filter: { id },
    limit: 1
  });
  
  if (!response.data.documents?.length) {
    throw new Error('Document not found');
  }
  
  const doc = response.data.documents[0];
  const commhubId = doc._id || doc.id;
  await commhub.delete(`/data/${collection}/${commhubId}`);
  invalidateCache(collection);
  return { success: true };
};

// Delete all documents in a collection (for cleanup)
export const clearCollection = async (collection: string): Promise<{ deleted: number }> => {
  const response = await commhub.post(`/data/${collection}/query`, {
    filter: {},
    limit: 1000
  });
  
  const docs = response.data.documents || [];
  let deleted = 0;
  
  for (const doc of docs) {
    try {
      const commhubId = doc._id || doc.id;
      await commhub.delete(`/data/${collection}/${commhubId}`);
      deleted++;
    } catch (e) {
      console.error('Failed to delete document:', e);
    }
  }
  
  invalidateCache(collection);
  return { deleted };
};

// Generic function to get all documents from a collection with optional filters
export const getCollection = async <T>(
  collection: string, 
  filters: Record<string, unknown> = {}
): Promise<ApiResponse<T[]>> => {
  const cacheKey = `${collection}_${JSON.stringify(filters)}`;
  const cached = getCached<T[]>(cacheKey);
  if (cached) return { data: cached };
  
  const response = await commhub.post(`/data/${collection}/query`, {
    filter: filters,
    limit: 1000
  });
  
  const documents = (response.data.documents || []).map((doc: { data: T } & Record<string, unknown>) => ({
    id: doc.id,
    ...doc.data
  })) as T[];
  
  setCache(cacheKey, documents);
  return { data: documents };
};

// ============ USERS ============

// Cache for CommHub users (shared between getUsers and getApartments)
let commHubUsersCache: { data: CommHubUserInternal[]; timestamp: number } | null = null;
const COMMHUB_USERS_CACHE_TTL = 30 * 1000; // 30 seconds

const getCommHubUsersCached = async (): Promise<CommHubUserInternal[]> => {
  // Return cached data if fresh
  if (commHubUsersCache && Date.now() - commHubUsersCache.timestamp < COMMHUB_USERS_CACHE_TTL) {
    return commHubUsersCache.data;
  }
  
  // Fetch fresh data - filter by app_id to only get users for this app
  try {
    const response = await commhub.get(`/users?app_id=${COMMHUB_APP_ID}&limit=500`);
    const data = response.data;
    
    let users: CommHubUserInternal[] = [];
    if (Array.isArray(data)) {
      users = data as CommHubUserInternal[];
    } else if (data && typeof data === 'object' && 'data' in data) {
      users = (data.data as CommHubUserInternal[]) || [];
    }
    
    // Update cache
    commHubUsersCache = { data: users, timestamp: Date.now() };
    return users;
  } catch (e) {
    console.error('Failed to fetch CommHub users:', e);
    return commHubUsersCache?.data || [];
  }
};

// Invalidate CommHub users cache (call after user mutations)
export const invalidateCommHubUsersCache = (): void => {
  commHubUsersCache = null;
};

export const getUsers = async (params: { role?: string; organization_id?: string } = {}): Promise<ApiResponse<User[]>> => {
  // Use cached CommHub users
  const commHubUsers = await getCommHubUsersCached();
  
  // Filter by params if provided
  let filteredUsers = commHubUsers;
  if (params.role) {
    filteredUsers = filteredUsers.filter(u => u.role === params.role);
  }
  if (params.organization_id) {
    filteredUsers = filteredUsers.filter(u => u.organization_id === params.organization_id);
  }
  
  // Convert to User format
  const users: User[] = filteredUsers.map(commHubUserToUserInternal);
  return { data: users };
};

export const getUser = async (id: string): Promise<ApiResponse<User | null>> => {
  // Try to get from cache first
  const cachedUsers = await getCommHubUsersCached();
  const cachedUser = cachedUsers.find(u => u.id === id);
  
  if (cachedUser) {
    return { data: commHubUserToUserInternal(cachedUser) };
  }
  
  // If not in cache, fetch directly
  try {
    const response = await commhub.get(`/users/${id}`);
    const commHubUser = response.data as CommHubUserInternal;
    return { data: commHubUserToUserInternal(commHubUser) };
  } catch (e) {
    console.error('Failed to fetch CommHub user:', e);
    return { data: null };
  }
};

export const createUser = async (userData: Partial<User>): Promise<ApiResponse<User>> => {
  // Use CommHub Users API
  try {
    // Split name into first_name and last_name
    const nameParts = (userData.name || '').trim().split(/\s+/);
    const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    
    const createData: CommHubUserCreate = {
      email: userData.email || '',
      first_name: firstName,
      last_name: lastName,
      phone: userData.phone,
      role: userData.role,
      organization_id: userData.organization_id,
      apartment_id: userData.apartment_id,
      property_id: userData.property_id,
      is_active: userData.is_active !== false
    };
    
    const response = await createCommHubUser(createData);
    // Invalidate cache after mutation
    invalidateCommHubUsersCache();
    
    // Convert CommHubUser response to User
    const commHubUser = response as unknown as CommHubUser;
    const createdUser = commHubUserToUser(commHubUser);
    
    // If organization_id wasn't set correctly, update the user
    if (userData.organization_id && commHubUser.organization_id !== userData.organization_id) {
      console.log('Organization ID not set on create, updating user...');
      try {
        await updateCommHubUser(createdUser.id, { 
          organization_id: userData.organization_id,
          apartment_id: userData.apartment_id
        });
        invalidateCommHubUsersCache();
        createdUser.organization_id = userData.organization_id;
      } catch (updateError) {
        console.warn('Failed to update organization_id after create:', updateError);
      }
    }
    
    return { data: createdUser };
  } catch (error) {
    console.error('Failed to create user in CommHub:', error);
    throw error;
  }
};

export const updateUser = async (id: string, userData: Partial<User>): Promise<ApiResponse<User>> => {
  // Use CommHub Users API
  try {
    // Split name into first_name and last_name if provided
    let firstName: string | undefined;
    let lastName: string | undefined;
    if (userData.name) {
      const nameParts = userData.name.trim().split(/\s+/);
      firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0];
      lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    }
    
    // Build update payload - use null for fields we want to clear
    const updatePayload: Record<string, unknown> = {};
    
    if (firstName !== undefined) updatePayload.first_name = firstName;
    if (lastName !== undefined) updatePayload.last_name = lastName;
    if (userData.email !== undefined) updatePayload.email = userData.email;
    if (userData.phone !== undefined) updatePayload.phone = userData.phone;
    if (userData.role !== undefined) updatePayload.role = userData.role;
    if (userData.is_active !== undefined) updatePayload.is_active = userData.is_active;
    
    // Handle organization_id - use empty string to explicitly clear it
    // CommHub API doesn't accept null, so we use empty string
    if ('organization_id' in userData) {
      updatePayload.organization_id = userData.organization_id === undefined ? '' : userData.organization_id;
    }
    if ('apartment_id' in userData) {
      updatePayload.apartment_id = userData.apartment_id === undefined ? '' : userData.apartment_id;
    }
    if ('property_id' in userData) {
      updatePayload.property_id = userData.property_id === undefined ? '' : userData.property_id;
    }
    if ('is_invoice_recipient' in userData) {
      updatePayload.is_invoice_recipient = userData.is_invoice_recipient || false;
    }
    
    const response = await updateCommHubUser(id, updatePayload);
    // Invalidate cache after mutation
    invalidateCommHubUsersCache();
    const commHubUser = response as unknown as CommHubUser;
    return { 
      data: commHubUserToUser(commHubUser)
    };
  } catch (error) {
    console.error('Failed to update user in CommHub:', error);
    throw error;
  }
};

export const deleteUser = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  // Use CommHub Users API
  try {
    await deleteCommHubUser(id);
    // Invalidate cache after mutation
    invalidateCommHubUsersCache();
    return { data: { success: true } };
  } catch (error) {
    console.error('Failed to delete user in CommHub:', error);
    throw error;
  }
};

// ============ PROPERTIES ============

export const getProperties = async (): Promise<ApiResponse<Property[]>> => {
  const data = await queryCollection<Property>('properties');
  return { data };
};

export const getProperty = async (id: string): Promise<ApiResponse<Property | null>> => {
  const data = await getDocument<Property>('properties', id);
  return { data };
};

export const createProperty = async (propertyData: Partial<Property>): Promise<ApiResponse<Property>> => {
  const data = await createDocument('properties', {
    ...propertyData,
    id: propertyData.id || crypto.randomUUID(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const updateProperty = async (id: string, propertyData: Partial<Property>): Promise<ApiResponse<Property>> => {
  const data = await updateDocument<Property>('properties', id, {
    ...propertyData,
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const deleteProperty = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('properties', id);
  return { data };
};

// ============ APARTMENTS ============

// Public version - for unauthenticated pages like MemberUpdatePage
export const getApartmentsPublic = async (organizationId: string): Promise<ApiResponse<Apartment[]>> => {
  try {
    console.log('getApartmentsPublic - fetching apartments for org:', organizationId);
    const apartments = await queryCollection<Apartment>('apartments', { organization_id: organizationId });
    console.log('getApartmentsPublic - found apartments:', apartments.length, apartments.map(a => a.apartment_number));
    return { data: apartments };
  } catch (error) {
    console.error('getApartmentsPublic - error:', error);
    return { data: [] };
  }
};

export const getApartmentsSimple = async (params: { property_id?: string; organization_id?: string } = {}): Promise<ApiResponse<Apartment[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.property_id) filter.property_id = params.property_id;
  if (params.organization_id) filter.organization_id = params.organization_id;
  
  const apartments = await queryCollection<Apartment>('apartments', filter);
  return { data: apartments };
};

export const getApartments = async (params: { property_id?: string; organization_id?: string } = {}): Promise<ApiResponse<Apartment[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.property_id) filter.property_id = params.property_id;
  if (params.organization_id) filter.organization_id = params.organization_id;
  
  const apartments = await queryCollection<Apartment>('apartments', filter);
  
  // Get CommHub users from cache (shared with getUsers)
  const commHubUsers = await getCommHubUsersCached();
  
  const apartmentsWithResidents = apartments.map(apt => {
    // Match users by apartment_id (which could be UUID or apartment_number)
    const aptResidents: User[] = commHubUsers.filter(user => {
      const userAptId = user.apartment_id;
      if (!userAptId) return false;
      // Direct match with apartment ID
      if (userAptId === apt.id) return true;
      // Match by apartment number
      if (userAptId === apt.apartment_number) return true;
      return false;
    }).map(user => ({
      id: user.id,
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.email,
      email: user.email,
      phone: user.phone,
      role: 'resident' as const,
      apartment_id: apt.id,
      is_active: user.is_active
    }));
    
    return {
      ...apt,
      residents: aptResidents,
      resident_count: aptResidents.length
    };
  });
  
  return { data: apartmentsWithResidents };
};

export const getApartment = async (id: string): Promise<ApiResponse<Apartment | null>> => {
  const apartment = await getDocument<Apartment>('apartments', id);
  if (!apartment) return { data: null };
  
  // Get CommHub users from cache (shared with getUsers/getApartments)
  const commHubUsers = await getCommHubUsersCached();
  
  // Find residents for this apartment
  const residents: User[] = commHubUsers.filter(user => {
    const userAptId = user.apartment_id;
    if (!userAptId) return false;
    if (userAptId === apartment.id) return true;
    if (userAptId === apartment.apartment_number) return true;
    return false;
  }).map(user => ({
    id: user.id,
    name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.email,
    email: user.email,
    phone: user.phone,
    role: 'resident' as const,
    apartment_id: apartment.id,
    is_active: user.is_active
  }));
  
  return { 
    data: {
      ...apartment,
      residents: residents,
      resident_count: residents.length
    }
  };
};

export const createApartment = async (apartmentData: Partial<Apartment>): Promise<ApiResponse<Apartment>> => {
  const data = await createDocument('apartments', {
    ...apartmentData,
    id: apartmentData.id || crypto.randomUUID(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const updateApartment = async (id: string, apartmentData: Partial<Apartment>): Promise<ApiResponse<Apartment>> => {
  const data = await updateDocument<Apartment>('apartments', id, {
    ...apartmentData,
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const deleteApartment = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('apartments', id);
  return { data };
};

export const removeResidentFromApartment = async (apartmentId: string, userId: string): Promise<ApiResponse<User>> => {
  const data = await updateDocument<User>('forvaltaren_users', userId, {
    apartment_id: undefined,
    updated_at: new Date().toISOString()
  });
  return { data };
};


// ============ ISSUES ============

interface IssueNotificationOptions {
  sendNotification?: boolean;
  reporterEmail?: string;
  organizationId?: string;
  organizationName?: string;
  previousIssue?: Issue | null;
}

const sendIssueNotification = async (issue: Issue, action: string, organizationId: string | null = null, organizationName: string | null = null): Promise<void> => {
  try {
    const filter: Record<string, unknown> = { role: 'admin' };
    if (organizationId) {
      filter.organization_id = organizationId;
    }
    const admins = await queryCollection<User>('forvaltaren_users', filter);
    
    if (admins.length === 0) {
      console.log('No admins found for notification');
      return;
    }
    
    const adminEmails = admins.filter(a => a.email).map(a => a.email as string);
    if (adminEmails.length === 0) return;
    
    const urgencyLabels: Record<number, string> = {
      1: 'Låg',
      2: 'Normal', 
      3: 'Medel',
      4: 'Hög',
      5: 'Akut'
    };
    
    const urgencyColors: Record<number, string> = {
      1: '#22c55e',
      2: '#84cc16',
      3: '#eab308',
      4: '#f97316',
      5: '#ef4444'
    };
    
    const urgency = issue.urgency_level || 2;
    const urgencyLabel = urgencyLabels[urgency] || 'Normal';
    const urgencyColor = urgencyColors[urgency] || '#84cc16';
    
    let subject: string | undefined;
    let htmlContent: string | undefined;
    
    if (action === 'created') {
      subject = `Nytt ärende: ${issue.title}`;
      htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #1e293b; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
            <h1 style="margin: 0; font-size: 20px;">Nytt ärende registrerat</h1>
          </div>
          <div style="background: #f8fafc; padding: 20px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
            <h2 style="margin: 0 0 10px; color: #1e293b;">${issue.title}</h2>
            <p style="color: #64748b; margin: 0 0 15px;">
              <span style="display: inline-block; padding: 4px 12px; background: ${urgencyColor}; color: white; border-radius: 20px; font-size: 12px; font-weight: 600;">
                ${urgencyLabel}
              </span>
              ${issue.category ? `<span style="margin-left: 8px; color: #64748b;">${issue.category}</span>` : ''}
            </p>
            <div style="background: white; padding: 15px; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 15px;">
              <p style="margin: 0; color: #334155; white-space: pre-wrap;">${issue.description || 'Ingen beskrivning'}</p>
            </div>
            <p style="margin: 0; font-size: 12px; color: #94a3b8;">
              Registrerat: ${new Date(issue.created_at || '').toLocaleString('sv-SE')}
            </p>
          </div>
        </div>
      `;
    } else if (action === 'status_changed') {
      const statusLabels: Record<string, string> = {
        'open': 'Öppen',
        'in_progress': 'Pågår',
        'resolved': 'Löst',
        'closed': 'Stängd'
      };
      subject = `Ärende uppdaterat: ${issue.title}`;
      htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #1e293b; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
            <h1 style="margin: 0; font-size: 20px;">Ärendestatus ändrad</h1>
          </div>
          <div style="background: #f8fafc; padding: 20px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
            <h2 style="margin: 0 0 10px; color: #1e293b;">${issue.title}</h2>
            <p style="color: #64748b; margin: 0 0 15px;">
              Status: <strong style="color: #1e293b;">${statusLabels[issue.status || ''] || issue.status}</strong>
            </p>
          </div>
        </div>
      `;
    }
    
    if (subject && htmlContent) {
      await sendEmail(adminEmails, subject, htmlContent, { fromName: organizationName || 'Förvaltningen' });
    }
  } catch (error) {
    console.error('sendIssueNotification error:', error);
  }
};

export const getIssues = async (params: { status?: string; category?: string; organization_id?: string; limit?: number; skip?: number } = {}): Promise<ApiResponse<Issue[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.status) filter.status = params.status;
  if (params.category) filter.category = params.category;
  if (params.organization_id) filter.organization_id = params.organization_id;
  
  const options: QueryOptions = {};
  if (params.limit) options.limit = params.limit;
  if (params.skip) options.skip = params.skip;
  
  const data = await queryCollection<Issue>('issues', filter, options);
  return { data };
};

export const getIssue = async (id: string): Promise<ApiResponse<Issue | null>> => {
  const data = await getDocument<Issue>('issues', id);
  return { data };
};

export const createIssue = async (issueData: Partial<Issue>, options: IssueNotificationOptions = {}): Promise<ApiResponse<Issue>> => {
  const { sendNotification = true, organizationId = null, organizationName = null } = options;
  
  const data = await createDocument('issues', {
    ...issueData,
    id: issueData.id || crypto.randomUUID(),
    status: issueData.status || 'open',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  
  if (sendNotification && data.id) {
    try {
      await sendIssueNotification(data as Issue, 'created', organizationId, organizationName);
    } catch (error) {
      console.error('Failed to send issue notification:', error);
    }
  }
  
  return { data };
};

export const updateIssue = async (id: string, issueData: Partial<Issue>, options: IssueNotificationOptions = {}): Promise<ApiResponse<Issue>> => {
  const { sendNotification = true, organizationId = null, organizationName = null, previousIssue = null } = options;
  
  const data = await updateDocument<Issue>('issues', id, {
    ...issueData,
    updated_at: new Date().toISOString()
  });
  
  if (sendNotification && previousIssue && issueData.status && previousIssue.status !== issueData.status) {
    try {
      await sendIssueNotification({ ...previousIssue, ...issueData, id } as Issue, 'status_changed', organizationId, organizationName);
    } catch (error) {
      console.error('Failed to send status change notification:', error);
    }
  }
  
  return { data };
};

export const deleteIssue = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('issues', id);
  return { data };
};

// ============ ORDERS ============

export const getOrders = async (params: { status?: string; issue_id?: string; organization_id?: string } = {}): Promise<ApiResponse<Order[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.status) filter.status = params.status;
  if (params.issue_id) filter.issue_id = params.issue_id;
  if (params.organization_id) filter.organization_id = params.organization_id;
  
  const data = await queryCollection<Order>('orders', filter);
  return { data };
};

export const getOrder = async (id: string): Promise<ApiResponse<Order | null>> => {
  const data = await getDocument<Order>('orders', id);
  return { data };
};

export const createOrder = async (orderData: Partial<Order>): Promise<ApiResponse<Order>> => {
  const data = await createDocument('orders', {
    ...orderData,
    id: orderData.id || crypto.randomUUID(),
    status: orderData.status || 'pending',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const getOrderByToken = async (token: string): Promise<ApiResponse<Order | null>> => {
  const orders = await queryCollection<Order>('orders', { confirmation_token: token }, { limit: 1 });
  return { data: orders[0] || null };
};

export const confirmOrder = async (token: string, confirmData: Partial<Order>): Promise<ApiResponse<Order>> => {
  const orders = await queryCollection<Order>('orders', { confirmation_token: token }, { limit: 1 });
  if (!orders.length) throw new Error('Order not found');
  
  const updateData: Partial<Order> = {
    ...confirmData,
    updated_at: new Date().toISOString()
  };
  
  if (confirmData.status === 'accepted') {
    updateData.status = 'accepted';
    updateData.confirmed_at = new Date().toISOString();
  } else if (confirmData.status === 'rejected') {
    updateData.status = 'rejected';
  } else {
    updateData.status = 'confirmed';
    updateData.confirmed_at = new Date().toISOString();
  }
  
  const data = await updateDocument<Order>('orders', orders[0].id, updateData);
  return { data };
};

export const sendOrderMessage = async (token: string, messageData: { text: string }): Promise<ApiResponse<{ id: string; text: string; created_at: string; from: string }>> => {
  const orders = await queryCollection<Order>('orders', { confirmation_token: token }, { limit: 1 });
  if (!orders.length) throw new Error('Order not found');
  
  const order = orders[0];
  const messages = order.supplier_messages || [];
  const newMessage = {
    id: crypto.randomUUID(),
    text: messageData.text,
    created_at: new Date().toISOString(),
    from: 'supplier'
  };
  messages.push(newMessage);
  
  await updateDocument<Order>('orders', order.id, {
    supplier_messages: messages,
    updated_at: new Date().toISOString()
  });
  
  return { data: newMessage };
};

export const submitOrderQuote = async (token: string, quoteData: { amount: number; description: string; attachments?: string[] }): Promise<ApiResponse<Order>> => {
  const orders = await queryCollection<Order>('orders', { confirmation_token: token }, { limit: 1 });
  if (!orders.length) throw new Error('Order not found');
  
  const data = await updateDocument<Order>('orders', orders[0].id, {
    status: 'waiting_quote_approval',
    quote_amount: quoteData.amount,
    quote_description: quoteData.description,
    quote_attachments: quoteData.attachments || [],
    updated_at: new Date().toISOString()
  });
  
  return { data };
};

export const completeOrder = async (token: string, completionData: { notes?: string; attachments?: string[] }): Promise<ApiResponse<Order>> => {
  const orders = await queryCollection<Order>('orders', { confirmation_token: token }, { limit: 1 });
  if (!orders.length) throw new Error('Order not found');
  
  const order = orders[0];
  const newStatus = order.quote_amount ? 'waiting_invoice' : 'completed';
  
  const data = await updateDocument<Order>('orders', order.id, {
    status: newStatus,
    completion_notes: completionData.notes,
    completion_attachments: completionData.attachments || [],
    completed_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  
  return { data };
};

// ============ ORGANIZATIONS ============

export const getOrganizations = async (): Promise<ApiResponse<Organization[]>> => {
  const data = await queryCollection<Organization>('organizations');
  return { data };
};

export const getOrganization = async (id: string): Promise<ApiResponse<Organization | null>> => {
  const data = await getDocument<Organization>('organizations', id);
  return { data };
};

export const createOrganization = async (orgData: Partial<Organization>): Promise<ApiResponse<Organization>> => {
  const data = await createDocument('organizations', {
    ...orgData,
    id: orgData.id || crypto.randomUUID(),
    status: orgData.status || 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const updateOrganization = async (id: string, orgData: Partial<Organization>): Promise<ApiResponse<Organization>> => {
  const data = await updateDocument<Organization>('organizations', id, {
    ...orgData,
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const deleteOrganization = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('organizations', id);
  return { data };
};

// ============ SUPPLIERS ============

export const getSuppliers = async (): Promise<ApiResponse<Supplier[]>> => {
  const data = await queryCollection<Supplier>('suppliers');
  return { data };
};

export const getSupplier = async (id: string): Promise<ApiResponse<Supplier | null>> => {
  const data = await getDocument<Supplier>('suppliers', id);
  return { data };
};

export const createSupplier = async (supplierData: Partial<Supplier>): Promise<ApiResponse<Supplier>> => {
  const data = await createDocument('suppliers', {
    ...supplierData,
    id: supplierData.id || crypto.randomUUID(),
    created_at: new Date().toISOString()
  });
  return { data };
};

export const updateSupplier = async (id: string, supplierData: Partial<Supplier>): Promise<ApiResponse<Supplier>> => {
  const data = await updateDocument<Supplier>('suppliers', id, supplierData);
  return { data };
};

export const deleteSupplier = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('suppliers', id);
  return { data };
};

// ============ FILE UPLOAD ============

export const uploadFile = async (file: File): Promise<{ url?: string; file_url?: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('app_id', COMMHUB_APP_ID);
  
  const response = await commhub.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  
  return response.data;
};

// ============ EMAIL ============

interface EmailOptions {
  fromName?: string;
  replyTo?: string;
}

export const sendEmail = async (to: string | string[], subject: string, htmlContent: string, options: EmailOptions = {}): Promise<unknown> => {
  const response = await commhub.post('/email/send', {
    app_id: COMMHUB_APP_ID,
    to: Array.isArray(to) ? to : [to],
    subject,
    html_content: htmlContent,
    from_name: options.fromName || 'Förvaltningen',
    reply_to: options.replyTo,
    channel: 'auto'
  });
  return response.data;
};

// ============ SMS ============

interface SmsOptions {
  senderName?: string;
}

export const sendSMS = async (to: string, message: string, options: SmsOptions = {}): Promise<unknown> => {
  const response = await commhub.post('/sms/send', {
    app_id: COMMHUB_APP_ID,
    to: to,
    message: message,
    sender_name: options.senderName || 'Forvaltning'
  });
  return response.data;
};

export const sendWorkOrderSMS = async (supplier: Supplier, issue: Issue, workOrderUrl: string): Promise<unknown> => {
  const message = `Nytt uppdrag: ${issue.title}. Se detaljer: ${workOrderUrl}`;
  
  if (!supplier.phone) {
    throw new Error('Leverantören saknar telefonnummer');
  }
  
  return await sendSMS(supplier.phone, message, { senderName: 'Forvaltning' });
};

// ============ RESIDENTS ============

interface ResidentOptions {
  logActivity?: boolean;
  previousData?: User | null;
  performed_by?: string;
}

export const createTenant = async (tenantData: Partial<User>, options: ResidentOptions = {}): Promise<ApiResponse<User>> => {
  const { logActivity: shouldLog = true } = options;
  
  // Extract name parts
  const firstName = (tenantData as { first_name?: string }).first_name || tenantData.name?.split(' ')[0] || '';
  const lastName = (tenantData as { last_name?: string }).last_name || tenantData.name?.split(' ').slice(1).join(' ') || '';
  
  // Use CommHub Users API instead of forvaltaren_users collection
  const commHubResponse = await createCommHubUser({
    first_name: firstName,
    last_name: lastName,
    email: tenantData.email || '',
    phone: tenantData.phone,
    apartment_id: tenantData.apartment_id,
    organization_id: tenantData.organization_id,
    role: (tenantData.role || 'resident') as 'superadmin' | 'admin' | 'auditor' | 'suppliant' | 'board_member' | 'resident' | 'supplier' | 'external',
    is_active: true
  });
  
  // createCommHubUser returns the user data directly (not wrapped in .data)
  const commHubData = commHubResponse as unknown as CommHubUser;
  
  // Convert to User format
  const data: User = {
    id: commHubData.id,
    name: `${commHubData.first_name || ''} ${commHubData.last_name || ''}`.trim(),
    first_name: commHubData.first_name,
    last_name: commHubData.last_name,
    email: commHubData.email,
    phone: commHubData.phone,
    role: commHubData.role as User['role'],
    apartment_id: commHubData.apartment_id,
    organization_id: commHubData.organization_id,
    is_active: commHubData.is_active,
    move_in_date: tenantData.move_in_date || new Date().toISOString(),
    created_at: new Date().toISOString()
  };
  
  if (shouldLog && data.id) {
    try {
      await logActivity({
        entity_type: 'resident',
        entity_id: data.id,
        action: 'moved_in',
        details: {
          description: `${data.name || 'Ny boende'} flyttade in`,
          name: data.name,
          email: data.email,
          phone: data.phone
        },
        user_id: data.id,
        apartment_id: data.apartment_id,
        property_id: tenantData.property_id,
        organization_id: data.organization_id,
        performed_by: options.performed_by
      });
    } catch (e) {
      console.error('Failed to log move-in activity:', e);
    }
  }
  
  return { data };
};

export const updateTenant = async (id: string, tenantData: Partial<User>, options: ResidentOptions = {}): Promise<ApiResponse<User>> => {
  const { logActivity: shouldLog = true, previousData = null } = options;
  
  const updateData = { ...tenantData };
  
  // Extract name parts
  const firstName = (updateData as { first_name?: string }).first_name || updateData.name?.split(' ')[0];
  const lastName = (updateData as { last_name?: string }).last_name || updateData.name?.split(' ').slice(1).join(' ');
  
  // Use CommHub Users API instead of forvaltaren_users collection
  const commHubResponse = await updateCommHubUser(id, {
    first_name: firstName,
    last_name: lastName,
    email: updateData.email,
    phone: updateData.phone,
    apartment_id: updateData.apartment_id,
    role: updateData.role,
    is_active: updateData.is_active
  });
  
  // updateCommHubUser returns the user data directly (not wrapped in .data)
  const data = commHubResponse as unknown as CommHubUser;
  
  if (shouldLog && data) {
    try {
      if (previousData && previousData.apartment_id !== tenantData.apartment_id) {
        if (previousData.apartment_id) {
          await logActivity({
            entity_type: 'resident',
            entity_id: id,
            action: 'moved_out',
            details: {
              description: `${data.first_name} ${data.last_name || ''} flyttade ut`,
              from_apartment: previousData.apartment_id
            },
            user_id: id,
            apartment_id: previousData.apartment_id,
            organization_id: data.organization_id,
            performed_by: options.performed_by
          });
        }
        if (tenantData.apartment_id) {
          await logActivity({
            entity_type: 'resident',
            entity_id: id,
            action: 'moved_in',
            details: {
              description: `${data.first_name} ${data.last_name || ''} flyttade in`,
              to_apartment: tenantData.apartment_id
            },
            user_id: id,
            apartment_id: tenantData.apartment_id,
            organization_id: data.organization_id,
            performed_by: options.performed_by
          });
        }
      }
    } catch (e) {
      console.error('Failed to log resident update activity:', e);
    }
  }
  
  // Convert CommHub user response to User format
  const user: User = {
    id: data.id,
    name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
    first_name: data.first_name,
    last_name: data.last_name,
    email: data.email,
    phone: data.phone,
    role: data.role as User['role'],
    apartment_id: data.apartment_id,
    organization_id: data.organization_id,
    is_active: data.is_active
  };
  
  return { data: user };
};

export const deleteTenant = async (id: string, options: ResidentOptions & { residentData?: User | null } = {}): Promise<ApiResponse<User>> => {
  const { logActivity: shouldLog = true, residentData = null } = options;
  
  if (shouldLog && residentData) {
    try {
      await logActivity({
        entity_type: 'resident',
        entity_id: id,
        action: 'moved_out',
        details: {
          description: `${residentData.name || 'Boende'} flyttade ut/avregistrerades`,
          move_out_date: new Date().toISOString()
        },
        user_id: id,
        apartment_id: residentData.apartment_id,
        organization_id: residentData.organization_id,
        performed_by: options.performed_by
      });
    } catch (e) {
      console.error('Failed to log move-out activity:', e);
    }
  }
  
  const data = await updateDocument<User>('forvaltaren_users', id, {
    is_active: false,
    move_out_date: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  
  return { data };
};

export const createResident = createTenant;
export const updateResident = updateTenant;
export const deleteResident = deleteTenant;

// ============ ACTIVITY LOG ============

export const logActivity = async (activityData: Partial<ActivityLog>): Promise<ApiResponse<ActivityLog>> => {
  const data = await createDocument('activity_log', {
    ...activityData,
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString()
  });
  return { data };
};

export const getActivityLog = async (params: { user_id?: string; apartment_id?: string; property_id?: string; organization_id?: string; entity_type?: string; limit?: number; skip?: number } = {}): Promise<ApiResponse<ActivityLog[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.user_id) filter.user_id = params.user_id;
  if (params.apartment_id) filter.apartment_id = params.apartment_id;
  if (params.property_id) filter.property_id = params.property_id;
  if (params.organization_id) filter.organization_id = params.organization_id;
  if (params.entity_type) filter.entity_type = params.entity_type;
  
  const data = await queryCollection<ActivityLog>('activity_log', filter, {
    limit: params.limit || 100,
    skip: params.skip || 0
  });
  
  const sorted = data.sort((a, b) => new Date(b.timestamp || '').getTime() - new Date(a.timestamp || '').getTime());
  return { data: sorted };
};

export const getUserTimeline = async (userId: string): Promise<ApiResponse<ActivityLog[]>> => {
  const activities = await queryCollection<ActivityLog>('activity_log', { user_id: userId });
  const sorted = activities.sort((a, b) => new Date(b.timestamp || '').getTime() - new Date(a.timestamp || '').getTime());
  return { data: sorted };
};

export const getApartmentHistory = async (apartmentId: string): Promise<ApiResponse<{ activities: ActivityLog[]; residents: User[] }>> => {
  const activities = await queryCollection<ActivityLog>('activity_log', { apartment_id: apartmentId });
  const allUsers = await queryCollection<User>('forvaltaren_users');
  const historicalResidents = allUsers.filter(u => 
    u.apartment_id === apartmentId || 
    u.previous_apartments?.includes(apartmentId)
  );
  
  const sorted = activities.sort((a, b) => new Date(b.timestamp || '').getTime() - new Date(a.timestamp || '').getTime());
  return { 
    data: {
      activities: sorted,
      residents: historicalResidents
    }
  };
};

export const getPropertyHistory = async (propertyId: string): Promise<ApiResponse<ActivityLog[]>> => {
  const activities = await queryCollection<ActivityLog>('activity_log', { property_id: propertyId });
  const sorted = activities.sort((a, b) => new Date(b.timestamp || '').getTime() - new Date(a.timestamp || '').getTime());
  return { data: sorted };
};

export const logResidentChange = async (apartmentId: string, userId: string, action: string, details: Record<string, unknown> = {}): Promise<ApiResponse<ActivityLog>> => {
  return await logActivity({
    entity_type: 'resident',
    entity_id: userId,
    apartment_id: apartmentId,
    action: action,
    details: details as ActivityLog['details'],
    user_id: userId
  });
};

// ============ DOCUMENTS ============

export const getDocuments = async (params: { category?: string; organization_id?: string; assigned_to_user?: string; assigned_to_apartment?: string; assigned_to_property?: string } = {}): Promise<ApiResponse<Document[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.category) filter.category = params.category;
  if (params.organization_id) filter.organization_id = params.organization_id;
  if (params.assigned_to_user) filter.assigned_to_user = params.assigned_to_user;
  if (params.assigned_to_apartment) filter.assigned_to_apartment = params.assigned_to_apartment;
  if (params.assigned_to_property) filter.assigned_to_property = params.assigned_to_property;
  
  const data = await queryCollection<Document>('documents', filter);
  return { data };
};

export const getDocumentById_exported = async (id: string): Promise<ApiResponse<Document | null>> => {
  const data = await getDocument<Document>('documents', id);
  return { data };
};

export const createDocumentRecord = async (docData: Partial<Document>): Promise<ApiResponse<Document>> => {
  const data = await createDocument('documents', {
    ...docData,
    id: docData.id || crypto.randomUUID(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const updateDocumentRecord = async (id: string, docData: Partial<Document>): Promise<ApiResponse<Document>> => {
  const data = await updateDocument<Document>('documents', id, {
    ...docData,
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const deleteDocumentRecord = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('documents', id);
  return { data };
};

export const searchDocuments = async (query: string): Promise<ApiResponse<Document[]>> => {
  if (!query || query.length < 2) return { data: [] };
  
  const searchLower = query.toLowerCase();
  const docs = await queryCollection<Document>('documents');
  
  const matched = docs.filter(d =>
    d.name?.toLowerCase().includes(searchLower) ||
    d.description?.toLowerCase().includes(searchLower) ||
    d.category?.toLowerCase().includes(searchLower) ||
    d.tags?.some(t => t.toLowerCase().includes(searchLower))
  );
  
  return { data: matched };
};

// ============ MAINTENANCE SCHEDULES ============

export const getMaintenanceSchedules = async (params: { organization_id?: string; property_id?: string; apartment_id?: string; is_active?: boolean } = {}): Promise<ApiResponse<MaintenanceSchedule[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.organization_id) filter.organization_id = params.organization_id;
  if (params.property_id) filter.property_id = params.property_id;
  if (params.apartment_id) filter.apartment_id = params.apartment_id;
  if (params.is_active !== undefined) filter.is_active = params.is_active;
  
  const data = await queryCollection<MaintenanceSchedule>('maintenance_schedules', filter);
  return { data };
};

export const createMaintenanceSchedule = async (scheduleData: Partial<MaintenanceSchedule>): Promise<ApiResponse<MaintenanceSchedule>> => {
  const data = await createDocument('maintenance_schedules', {
    ...scheduleData,
    id: scheduleData.id || crypto.randomUUID(),
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const updateMaintenanceSchedule = async (id: string, scheduleData: Partial<MaintenanceSchedule>): Promise<ApiResponse<MaintenanceSchedule>> => {
  const data = await updateDocument<MaintenanceSchedule>('maintenance_schedules', id, {
    ...scheduleData,
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const deleteMaintenanceSchedule = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('maintenance_schedules', id);
  return { data };
};

// Date helpers
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

const addYears = (date: Date, years: number): Date => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

const calculateNextOccurrence = (schedule: MaintenanceSchedule): Date | null => {
  const now = new Date();
  const lastCompleted = schedule.last_completed ? new Date(schedule.last_completed) : null;
  const startDate = schedule.start_date ? new Date(schedule.start_date) : now;
  
  const baseDate = lastCompleted || startDate;
  
  switch (schedule.frequency) {
    case 'daily':
      return addDays(baseDate, schedule.interval || 1);
    case 'weekly':
      return addDays(baseDate, (schedule.interval || 1) * 7);
    case 'monthly':
      return addMonths(baseDate, schedule.interval || 1);
    case 'quarterly':
      return addMonths(baseDate, (schedule.interval || 1) * 3);
    case 'yearly':
      return addYears(baseDate, schedule.interval || 1);
    case 'once':
      return schedule.due_date ? new Date(schedule.due_date) : null;
    default:
      return null;
  }
};

export const getUpcomingMaintenance = async (params: { organization_id?: string; days_ahead?: number } = {}): Promise<ApiResponse<MaintenanceSchedule[]>> => {
  const schedules = await queryCollection<MaintenanceSchedule>('maintenance_schedules', {
    organization_id: params.organization_id,
    is_active: true
  });
  
  const now = new Date();
  const upcoming: MaintenanceSchedule[] = [];
  
  for (const schedule of schedules) {
    const nextOccurrence = calculateNextOccurrence(schedule);
    if (nextOccurrence) {
      upcoming.push({
        ...schedule,
        next_date: nextOccurrence,
        days_until: Math.ceil((nextOccurrence.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      });
    }
  }
  
  upcoming.sort((a, b) => new Date(a.next_date || '').getTime() - new Date(b.next_date || '').getTime());
  
  if (params.days_ahead) {
    const cutoff = new Date(now.getTime() + params.days_ahead * 24 * 60 * 60 * 1000);
    return { data: upcoming.filter(u => new Date(u.next_date || '').getTime() <= cutoff.getTime()) };
  }
  
  return { data: upcoming };
};

export const completeMaintenanceSchedule = async (id: string, completionData: { completed_by?: string; notes?: string; current_count?: number; title?: string; apartment_id?: string; property_id?: string; organization_id?: string } = {}): Promise<ApiResponse<MaintenanceSchedule>> => {
  const data = await updateDocument<MaintenanceSchedule>('maintenance_schedules', id, {
    last_completed: new Date().toISOString(),
    last_completed_by: completionData.completed_by,
    last_completion_notes: completionData.notes,
    completion_count: (completionData.current_count || 0) + 1,
    updated_at: new Date().toISOString()
  });
  
  try {
    await logActivity({
      entity_type: 'maintenance',
      entity_id: id,
      action: 'completed',
      details: {
        description: `Underhåll utfört: ${completionData.title || 'Okänt'}`,
        notes: completionData.notes
      },
      apartment_id: completionData.apartment_id,
      property_id: completionData.property_id,
      organization_id: completionData.organization_id,
      performed_by: completionData.completed_by
    });
  } catch (e) {
    console.error('Failed to log maintenance completion:', e);
  }
  
  return { data };
};

// ============ COMPONENTS ============

export const BUILDING_PARTS = [
  { id: 'roof', name: 'Tak', icon: '🏠' },
  { id: 'facade', name: 'Fasad', icon: '🧱' },
  { id: 'windows', name: 'Fönster & Dörrar', icon: '🪟' },
  { id: 'foundation', name: 'Grund', icon: '🏗️' },
  { id: 'plumbing', name: 'VVS', icon: '🚿' },
  { id: 'electrical', name: 'El', icon: '⚡' },
  { id: 'hvac', name: 'Ventilation & Värme', icon: '🌡️' },
  { id: 'interior', name: 'Interiör', icon: '🛋️' },
  { id: 'outdoor', name: 'Utemiljö', icon: '🌳' },
  { id: 'safety', name: 'Säkerhet & Brand', icon: '🔒' }
];

export const getComponents = async (params: { organization_id?: string; property_id?: string; apartment_id?: string; building_part?: string } = {}): Promise<ApiResponse<Component[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.organization_id) filter.organization_id = params.organization_id;
  if (params.property_id) filter.property_id = params.property_id;
  if (params.apartment_id) filter.apartment_id = params.apartment_id;
  if (params.building_part) filter.building_part = params.building_part;
  
  const data = await queryCollection<Component>('components', filter);
  return { data };
};

export const createComponent = async (componentData: Partial<Component>): Promise<ApiResponse<Component>> => {
  const data = await createDocument('components', {
    ...componentData,
    id: componentData.id || crypto.randomUUID(),
    status: componentData.status || 'ok',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const updateComponent = async (id: string, componentData: Partial<Component>): Promise<ApiResponse<Component>> => {
  const data = await updateDocument<Component>('components', id, {
    ...componentData,
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const deleteComponent = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('components', id);
  return { data };
};

// ============ INSPECTION PROTOCOLS ============

export const getInspectionProtocols = async (params: { organization_id?: string; property_id?: string; apartment_id?: string; type?: string } = {}): Promise<ApiResponse<InspectionProtocol[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.organization_id) filter.organization_id = params.organization_id;
  if (params.property_id) filter.property_id = params.property_id;
  if (params.apartment_id) filter.apartment_id = params.apartment_id;
  if (params.type) filter.type = params.type;
  
  const data = await queryCollection<InspectionProtocol>('inspection_protocols', filter);
  const sorted = data.sort((a, b) => new Date(b.inspection_date || '').getTime() - new Date(a.inspection_date || '').getTime());
  return { data: sorted };
};

export const getInspectionProtocol = async (id: string): Promise<ApiResponse<InspectionProtocol | null>> => {
  const data = await getDocument<InspectionProtocol>('inspection_protocols', id);
  return { data };
};

export const createInspectionProtocol = async (protocolData: Partial<InspectionProtocol>): Promise<ApiResponse<InspectionProtocol>> => {
  const data = await createDocument('inspection_protocols', {
    ...protocolData,
    id: protocolData.id || crypto.randomUUID(),
    status: 'draft' as const,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  return { data: data as InspectionProtocol };
};

export const updateInspectionProtocol = async (id: string, protocolData: Partial<InspectionProtocol>): Promise<ApiResponse<InspectionProtocol>> => {
  const data = await updateDocument<InspectionProtocol>('inspection_protocols', id, {
    ...protocolData,
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const deleteInspectionProtocol = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('inspection_protocols', id);
  return { data };
};

export const finalizeInspectionProtocol = async (id: string, protocolData: Partial<InspectionProtocol>, options: { createMaintenanceActions?: boolean; finalized_by?: string } = {}): Promise<ApiResponse<InspectionProtocol>> => {
  const { createMaintenanceActions = false } = options;
  
  const data = await updateDocument<InspectionProtocol>('inspection_protocols', id, {
    ...protocolData,
    status: 'completed',
    finalized_at: new Date().toISOString(),
    finalized_by: options.finalized_by,
    updated_at: new Date().toISOString()
  });
  
  if (createMaintenanceActions && protocolData.items) {
    const actionItems = protocolData.items.filter(item => 
      item.status === 'action_required' || item.status === 'urgent'
    );
    
    for (const item of actionItems) {
      try {
        await createMaintenanceSchedule({
          title: item.recommended_action || `Åtgärd: ${item.component_name}`,
          description: item.comment,
          category: item.building_part || 'other',
          frequency: 'once',
          property_id: protocolData.property_id,
          apartment_id: protocolData.apartment_id,
          organization_id: protocolData.organization_id,
          due_date: item.priority === 'urgent' ? 
            new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() :
            new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          source_protocol_id: id,
          estimated_cost: item.estimated_cost
        });
      } catch (e) {
        console.error('Failed to create maintenance action:', e);
      }
    }
  }
  
  try {
    await logActivity({
      entity_type: 'inspection',
      entity_id: id,
      action: 'completed',
      details: {
        description: `Inspektion slutförd: ${protocolData.type || 'Kontroll'}`,
        items_count: String(protocolData.items?.length || 0),
        actions_created: String(createMaintenanceActions ? 
          protocolData.items?.filter(i => i.status === 'action_required' || i.status === 'urgent').length : 0)
      },
      property_id: protocolData.property_id,
      apartment_id: protocolData.apartment_id,
      organization_id: protocolData.organization_id,
      performed_by: options.finalized_by
    });
  } catch (e) {
    console.error('Failed to log inspection completion:', e);
  }
  
  return { data };
};

// ============ ACTION PROTOCOLS ============

export const getActionProtocols = async (params: { organization_id?: string; property_id?: string; apartment_id?: string; component_id?: string } = {}): Promise<ApiResponse<ActionProtocol[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.organization_id) filter.organization_id = params.organization_id;
  if (params.property_id) filter.property_id = params.property_id;
  if (params.apartment_id) filter.apartment_id = params.apartment_id;
  if (params.component_id) filter.component_id = params.component_id;
  
  const data = await queryCollection<ActionProtocol>('action_protocols', filter);
  const sorted = data.sort((a, b) => new Date(b.action_date || '').getTime() - new Date(a.action_date || '').getTime());
  return { data: sorted };
};

export const createActionProtocol = async (actionData: Partial<ActionProtocol>): Promise<ApiResponse<ActionProtocol>> => {
  const data = await createDocument('action_protocols', {
    ...actionData,
    id: actionData.id || crypto.randomUUID(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  
  if (actionData.component_id && actionData.new_status) {
    try {
      await updateComponent(actionData.component_id, {
        status: actionData.new_status as Component['status'],
        last_action_date: new Date().toISOString()
      });
    } catch (e) {
      console.error('Failed to update component status:', e);
    }
  }
  
  try {
    await logActivity({
      entity_type: 'action',
      entity_id: data.id,
      action: 'created',
      details: {
        description: `Åtgärd utförd: ${actionData.title || actionData.action_type}`,
        cost: String(actionData.cost || 0),
        performed_by_name: actionData.performed_by_name
      },
      property_id: actionData.property_id,
      apartment_id: actionData.apartment_id,
      organization_id: actionData.organization_id,
      performed_by: actionData.performed_by
    });
  } catch (e) {
    console.error('Failed to log action protocol:', e);
  }
  
  return { data };
};

export const updateActionProtocol = async (id: string, actionData: Partial<ActionProtocol>): Promise<ApiResponse<ActionProtocol>> => {
  const data = await updateDocument<ActionProtocol>('action_protocols', id, {
    ...actionData,
    updated_at: new Date().toISOString()
  });
  return { data };
};

export const deleteActionProtocol = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('action_protocols', id);
  return { data };
};

// ============ STATS & DATA AGGREGATION ============

export const getStats = async (): Promise<ApiResponse<DashboardStats>> => {
  const [issues, properties, apartments, users] = await Promise.all([
    queryCollection<Issue>('issues'),
    queryCollection<Property>('properties'),
    queryCollection<Apartment>('apartments'),
    queryCollection<User>('forvaltaren_users')
  ]);
  
  const totalIssues = issues.length;
  const openIssues = issues.filter(i => i.status === 'open').length;
  const inProgress = issues.filter(i => i.status === 'in_progress').length;
  const resolved = issues.filter(i => i.status === 'resolved').length;
  const emergency = issues.filter(i => (i.urgency_level || 0) >= 4 && !['resolved', 'closed'].includes(i.status || '')).length;
  
  const byCategory: Record<string, number> = {};
  issues.forEach(i => {
    const cat = i.category || 'Övrigt';
    byCategory[cat] = (byCategory[cat] || 0) + 1;
  });
  
  const byUrgency: Record<string, number> = {};
  issues.forEach(i => {
    const urg = String(i.urgency_level || 1);
    byUrgency[urg] = (byUrgency[urg] || 0) + 1;
  });
  
  return {
    data: {
      issues: {
        total: totalIssues,
        open: openIssues,
        in_progress: inProgress,
        resolved: resolved,
        emergency: emergency,
        by_category: byCategory,
        by_urgency: byUrgency
      },
      properties: properties.length,
      apartments: apartments.length,
      users: users.length
    }
  };
};

export const getAlerts = async (): Promise<ApiResponse<AlertsData>> => {
  const [issues, emailThreads] = await Promise.all([
    queryCollection<Issue>('issues'),
    queryCollection<EmailThread>('email_threads')
  ]);
  
  const activeIssues = issues.filter(i => ['open', 'in_progress'].includes(i.status || '')).length;
  const urgentIssues = issues.filter(i => (i.urgency_level || 0) >= 4 && !['resolved', 'closed'].includes(i.status || '')).length;
  const unreadMessages = emailThreads.filter(t => !t.is_read).length;
  
  return {
    data: {
      active_issues: activeIssues,
      urgent_issues: urgentIssues,
      unread_messages: unreadMessages,
      urgent_messages: 0
    }
  };
};

export const getProtocolStats = async (params: { organization_id?: string } = {}): Promise<ApiResponse<ProtocolStats>> => {
  const [inspections, actions, components] = await Promise.all([
    queryCollection<InspectionProtocol>('inspection_protocols', { organization_id: params.organization_id }),
    queryCollection<ActionProtocol>('action_protocols', { organization_id: params.organization_id }),
    queryCollection<Component>('components', { organization_id: params.organization_id })
  ]);
  
  const stats: ProtocolStats = {
    total_inspections: inspections.length,
    completed_inspections: inspections.filter(i => i.status === 'completed').length,
    draft_inspections: inspections.filter(i => i.status === 'draft').length,
    total_actions: actions.length,
    total_cost: actions.reduce((sum, a) => sum + (parseFloat(String(a.cost)) || 0), 0),
    components_ok: components.filter(c => c.status === 'ok').length,
    components_minor: components.filter(c => c.status === 'minor_defect').length,
    components_action: components.filter(c => c.status === 'action_required').length,
    components_urgent: components.filter(c => c.status === 'urgent').length
  };
  
  return { data: stats };
};

export const globalSearch = async (q: string): Promise<ApiResponse<{ issues: Issue[]; residents: User[]; tenants: User[]; apartments: Apartment[] }>> => {
  if (!q || q.length < 2) return { data: { issues: [], residents: [], tenants: [], apartments: [] } };
  
  const searchLower = q.toLowerCase();
  
  const [issues, users, apartments] = await Promise.all([
    queryCollection<Issue>('issues'),
    queryCollection<User>('forvaltaren_users'),
    queryCollection<Apartment>('apartments')
  ]);
  
  const matchedIssues = issues.filter(i => 
    i.title?.toLowerCase().includes(searchLower) ||
    i.description?.toLowerCase().includes(searchLower)
  ).slice(0, 10);
  
  const matchedResidents = users.filter(u =>
    u.name?.toLowerCase().includes(searchLower) ||
    u.email?.toLowerCase().includes(searchLower) ||
    u.phone?.includes(searchLower)
  ).slice(0, 10);
  
  const matchedApartments = apartments.filter(a =>
    a.apartment_number?.toLowerCase().includes(searchLower) ||
    (a as unknown as { address?: string }).address?.toLowerCase().includes(searchLower)
  ).slice(0, 10);
  
  return { 
    data: { 
      issues: matchedIssues, 
      residents: matchedResidents,
      tenants: matchedResidents,
      apartments: matchedApartments 
    } 
  };
};

// ============ EMAIL THREADS ============

export const getEmailThreads = async (): Promise<ApiResponse<EmailThread[]>> => {
  const data = await queryCollection<EmailThread>('email_threads');
  return { data };
};

export const getEmailThread = async (id: string): Promise<ApiResponse<EmailThread | null>> => {
  const data = await getDocument<EmailThread>('email_threads', id);
  return { data };
};

export const deleteEmailThread = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('email_threads', id);
  return { data };
};

// ============ SERVICE MESSAGES ============

export const getServiceMessages = async (params: { limit?: number } = {}): Promise<ApiResponse<ServiceMessage[]>> => {
  const options: QueryOptions = {};
  if (params.limit) options.limit = params.limit;
  const data = await queryCollection<ServiceMessage>('service_messages', {}, options);
  return { data };
};

export const createServiceMessage = async (messageData: Partial<ServiceMessage>): Promise<ApiResponse<ServiceMessage>> => {
  const data = await createDocument('service_messages', {
    ...messageData,
    id: crypto.randomUUID(),
    created_at: new Date().toISOString()
  });
  return { data };
};

// ============ AUDIT LOG ============

export const getAuditLogs = async (params: { category?: string; organization_id?: string; limit?: number; skip?: number } = {}): Promise<ApiResponse<AuditLog[]>> => {
  const filter: Record<string, unknown> = {};
  if (params.category) filter.category = params.category;
  if (params.organization_id) filter.organization_id = params.organization_id;
  
  const data = await queryCollection<AuditLog>('audit_logs', filter, { 
    limit: params.limit || 100,
    skip: params.skip || 0
  });
  return { data };
};

export const getAuditCategories = async (): Promise<ApiResponse<string[]>> => {
  return { 
    data: ['auth', 'issues', 'users', 'apartments', 'properties', 'orders', 'system'] 
  };
};

export const getAuditOrganizations = async (): Promise<ApiResponse<Organization[]>> => {
  const data = await queryCollection<Organization>('organizations');
  return { data };
};

export const getAuditStats = async (): Promise<ApiResponse<{ total: number; by_category: Record<string, number> }>> => {
  const logs = await queryCollection<AuditLog>('audit_logs');
  const byCategory: Record<string, number> = {};
  logs.forEach(log => {
    const cat = log.category || 'other';
    byCategory[cat] = (byCategory[cat] || 0) + 1;
  });
  return { data: { total: logs.length, by_category: byCategory } };
};

// ============ EMERGENCY CONTACTS ============

export const getEmergencyContacts = async (): Promise<ApiResponse<EmergencyContact[]>> => {
  const data = await queryCollection<EmergencyContact>('emergency_contacts');
  return { data };
};

// ============ API KEYS ============

export const getApiKeys = async (): Promise<ApiResponse<ApiKey[]>> => {
  const data = await queryCollection<ApiKey>('api_keys');
  return { data };
};

export const createApiKey = async (keyData: Partial<ApiKey>): Promise<ApiResponse<ApiKey & { key: string }>> => {
  const key = `fv_${crypto.randomUUID().replace(/-/g, '')}`;
  const data = await createDocument('api_keys', {
    ...keyData,
    id: crypto.randomUUID(),
    key: key,
    created_at: new Date().toISOString()
  });
  return { data: { ...data, key } };
};

export const deleteApiKey = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('api_keys', id);
  return { data };
};

// ============ INVITATIONS ============

export const getInvitations = async (): Promise<ApiResponse<Invitation[]>> => {
  const data = await queryCollection<Invitation>('invitations');
  return { data };
};

export const createInvitation = async (inviteData: Partial<Invitation>): Promise<ApiResponse<Invitation>> => {
  const data = await createDocument('invitations', {
    ...inviteData,
    id: crypto.randomUUID(),
    status: 'pending' as const,
    created_at: new Date().toISOString()
  });
  return { data: data as Invitation };
};

export const deleteInvitation = async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
  const data = await deleteDocument('invitations', id);
  return { data };
};

// ============ ISSUE NOTES ============

export const createIssueNote = async (issueId: string, noteData: { text: string; author_name?: string }): Promise<ApiResponse<{ id: string; issue_id: string; text: string; author_name?: string; created_at: string }>> => {
  const data = await createDocument('issue_notes', {
    ...noteData,
    id: crypto.randomUUID(),
    issue_id: issueId,
    created_at: new Date().toISOString()
  });
  return { data };
};

// ============ EMAIL STATS ============

export const getEmailStats = async (): Promise<ApiResponse<{ total_sent: number; this_week: number; by_day: Record<string, number> }>> => {
  const threads = await queryCollection<EmailThread>('email_threads');
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  
  const recent = threads.filter(t => new Date(t.created_at || '').getTime() > weekAgo.getTime());
  return {
    data: {
      total_sent: threads.length,
      this_week: recent.length,
      by_day: {}
    }
  };
};

// ============ DEV SETTINGS ============

export const getDevSettings = async (): Promise<DevSettings> => {
  try {
    const settings = await queryCollection<DevSettings>('dev_settings', { id: 'global' }, { limit: 1 });
    return settings[0] || { dev_login_enabled: false };
  } catch (error) {
    console.error('Failed to get dev settings:', error);
    return { dev_login_enabled: false };
  }
};

export const updateDevSettings = async (settings: Partial<DevSettings>): Promise<{ success: boolean }> => {
  try {
    const response = await commhub.post('/data/dev_settings/query', {
      filter: { id: 'global' },
      limit: 1
    });
    
    if (response.data.documents?.length > 0) {
      const docId = response.data.documents[0].id;
      await commhub.patch(`/data/dev_settings/${docId}`, {
        ...settings,
        updated_at: new Date().toISOString()
      });
    } else {
      await commhub.post('/data/dev_settings', {
        data: {
          id: 'global',
          ...settings,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      });
    }
    invalidateCache('dev_settings');
    return { success: true };
  } catch (error) {
    console.error('Failed to update dev settings:', error);
    return { success: false };
  }
};

export const devLogin = async (role: string): Promise<{ user: User; token: string }> => {
  // Try to get user from CommHub Users API
  let user: User;
  try {
    const commHubUsers = await getCommHubUsers({ role, limit: 1 });
    const userList = commHubUsers as unknown as CommHubUser[];
    if (userList.length > 0) {
      const u = userList[0];
      user = {
        id: u.id,
        email: u.email,
        name: u.name || '',
        role: u.role as User['role'] || 'resident',
        organization_id: u.organization_id,
        apartment_id: u.apartment_id,
        property_id: u.property_id,
        is_active: u.is_active !== false
      };
    } else {
      // Create a dev user if none found
      user = {
        id: `dev-${role}`,
        email: `dev-${role}@forvaltningen.se`,
        name: `DEV ${role.charAt(0).toUpperCase() + role.slice(1)}`,
        role: role as User['role'],
        organization_id: role === 'superadmin' ? undefined : '54d5e83e-1245-4ae7-b6e6-5d5e06795fff',
        is_active: true
      };
    }
  } catch {
    // Fallback to legacy collection
    const users = await queryCollection<User>('forvaltaren_users', { role }, { limit: 1 });
    if (users.length > 0) {
      user = users[0];
    } else {
      user = {
        id: `dev-${role}`,
        email: `dev-${role}@forvaltningen.se`,
        name: `DEV ${role.charAt(0).toUpperCase() + role.slice(1)}`,
        role: role as User['role'],
        organization_id: role === 'superadmin' ? undefined : '54d5e83e-1245-4ae7-b6e6-5d5e06795fff',
        is_active: true
      };
    }
  }
  
  const token = btoa(JSON.stringify({ sub: user.email, role: user.role, dev: true }));
  
  return { user, token };
};

// ============ BATCH DATA FUNCTIONS ============

export const getSuperAdminData = async (): Promise<{
  organizations: Organization[];
  users: User[];
  apartments: Apartment[];
  issues: Issue[];
  properties: Property[];
}> => {
  const results = await batchQuery([
    { collection: 'organizations', filter: {}, limit: 100 },
    { collection: 'apartments', filter: {}, limit: 500 },
    { collection: 'issues', filter: {}, limit: 500 },
    { collection: 'properties', filter: {}, limit: 500 }
  ]);
  
  // Fetch users from CommHub
  let users: User[] = [];
  try {
    const commHubUsers = await getCommHubUsers({ limit: 500 });
    users = (commHubUsers as unknown as CommHubUser[]).map(commHubUserToUser);
  } catch {
    users = await queryCollection<User>('forvaltaren_users');
  }
  
  return {
    organizations: (results.organizations || []) as Organization[],
    users,
    apartments: (results.apartments || []) as Apartment[],
    issues: (results.issues || []) as Issue[],
    properties: (results.properties || []) as Property[]
  };
};

export const getDashboardData = async (organizationId: string | null = null): Promise<{
  stats: DashboardStats;
  recentIssues: Issue[];
  emergencyContacts: EmergencyContact[];
  issues: Issue[];
  apartments: Apartment[];
  properties: Property[];
  users: User[];
}> => {
  // Fetch issues, apartments, properties, suppliers from collections
  const queries = [
    { collection: 'issues', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'apartments', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'properties', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'suppliers', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 }
  ];
  
  const results = await batchQuery(queries);
  
  const issues = (results.issues || []) as Issue[];
  const apartments = (results.apartments || []) as Apartment[];
  const properties = (results.properties || []) as Property[];
  const suppliers = (results.suppliers || []) as Array<{
    id?: string;
    name?: string;
    phone?: string;
    emergency_phone?: string;
    category?: string;
    is_emergency_contact?: boolean;
    is_active?: boolean;
  }>;
  
  // Filter suppliers that are marked as emergency contacts
  const emergencyContacts: EmergencyContact[] = suppliers
    .filter(s => s.is_emergency_contact && s.is_active !== false)
    .map(s => {
      const categoryKey = s.category || '';
      const categoryName = SUPPLIER_CATEGORIES[categoryKey] || categoryKey || 'Övrigt';
      return {
        id: s.id || '',
        name: s.name || '',
        phone: s.emergency_phone || s.phone || '',
        category: categoryKey,
        description: categoryName
      };
    });
  
  // Fetch users from CommHub Users API
  let users: User[] = [];
  try {
    const commHubUsers = await getCommHubUsers({ 
      organization_id: organizationId || undefined,
      limit: 500 
    });
    // Convert CommHubUser to User format
    users = (commHubUsers as unknown as CommHubUser[]).map(commHubUserToUser);
  } catch (error) {
    console.warn('Failed to fetch CommHub users, falling back to legacy collection:', error);
    // Fallback to legacy collection
    const legacyUsers = await queryCollection<User>('forvaltaren_users', 
      organizationId ? { organization_id: organizationId } : {}
    );
    users = legacyUsers;
  }
  
  const stats: DashboardStats = {
    issues: {
      total: issues.length,
      open: issues.filter(i => i.status === 'open').length,
      in_progress: issues.filter(i => i.status === 'in_progress').length,
      resolved: issues.filter(i => i.status === 'resolved').length,
      emergency: issues.filter(i => (i.urgency_level || 0) >= 4 && !['resolved', 'closed'].includes(i.status || '')).length
    },
    properties: properties.length,
    apartments: apartments.length,
    users: users.length
  };
  
  const recentIssues = [...issues]
    .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
    .slice(0, 5);
  
  return {
    stats,
    recentIssues,
    emergencyContacts,
    issues,
    apartments,
    properties,
    users
  };
};

export const getDocumentsPageData = async (organizationId: string | null): Promise<{
  documents: Document[];
  categories: string[];
  users: User[];
  apartments: Apartment[];
  properties: Property[];
}> => {
  const queries = [
    { collection: 'documents', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'apartments', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'properties', filter: {}, limit: 500 }
  ];
  
  const results = await batchQuery(queries);
  
  const defaultCategories = ['Avtal', 'Protokoll', 'Ritningar', 'Manualer', 'Övriga'];
  const documents = (results.documents || []) as Document[];
  const existingCategories = [...new Set(documents.map(d => d.category).filter(Boolean))] as string[];
  const allCategories = [...new Set([...defaultCategories, ...existingCategories])].sort();
  
  // Fetch users from CommHub
  const usersResponse = await getUsers({ organization_id: organizationId || undefined });
  const users = usersResponse.data || [];
  
  return {
    documents,
    categories: allCategories,
    users,
    apartments: (results.apartments || []) as Apartment[],
    properties: (results.properties || []) as Property[]
  };
};

export const getProtocolsPageData = async (organizationId: string | null): Promise<{
  protocols: InspectionProtocol[];
  actions: ActionProtocol[];
  components: Component[];
  properties: Property[];
  apartments: Apartment[];
  stats: ProtocolStats;
}> => {
  const queries = [
    { collection: 'inspection_protocols', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'action_protocols', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'components', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'properties', filter: {}, limit: 500 },
    { collection: 'apartments', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 }
  ];
  
  const results = await batchQuery(queries);
  
  const protocols = (results.inspection_protocols || []) as InspectionProtocol[];
  const actions = (results.action_protocols || []) as ActionProtocol[];
  const components = (results.components || []) as Component[];
  
  const stats: ProtocolStats = {
    total_inspections: protocols.length,
    completed_inspections: protocols.filter(i => i.status === 'completed').length,
    draft_inspections: protocols.filter(i => i.status === 'draft').length,
    total_actions: actions.length,
    total_cost: actions.reduce((sum, a) => sum + (parseFloat(String(a.cost)) || 0), 0),
    components_ok: components.filter(c => c.status === 'ok').length,
    components_minor: components.filter(c => c.status === 'minor_defect').length,
    components_action: components.filter(c => c.status === 'action_required').length,
    components_urgent: components.filter(c => c.status === 'urgent').length
  };
  
  return {
    protocols: protocols.sort((a, b) => new Date(b.inspection_date || '').getTime() - new Date(a.inspection_date || '').getTime()),
    actions: actions.sort((a, b) => new Date(b.action_date || '').getTime() - new Date(a.action_date || '').getTime()),
    components,
    properties: (results.properties || []) as Property[],
    apartments: (results.apartments || []) as Apartment[],
    stats
  };
};

export const getMaintenancePageData = async (organizationId: string | null): Promise<{
  schedules: MaintenanceSchedule[];
  upcomingEvents: MaintenanceSchedule[];
  properties: Property[];
  apartments: Apartment[];
}> => {
  const queries = [
    { collection: 'maintenance_schedules', filter: organizationId ? { organization_id: organizationId, is_active: true } : { is_active: true }, limit: 500 },
    { collection: 'properties', filter: {}, limit: 500 },
    { collection: 'apartments', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 }
  ];
  
  const results = await batchQuery(queries);
  const schedules = (results.maintenance_schedules || []) as MaintenanceSchedule[];
  
  const now = new Date();
  const upcoming: MaintenanceSchedule[] = [];
  
  for (const schedule of schedules) {
    const nextOccurrence = calculateNextOccurrence(schedule);
    if (nextOccurrence) {
      upcoming.push({
        ...schedule,
        next_date: nextOccurrence,
        days_until: Math.ceil((nextOccurrence.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      });
    }
  }
  
  upcoming.sort((a, b) => new Date(a.next_date || '').getTime() - new Date(b.next_date || '').getTime());
  
  return {
    schedules,
    upcomingEvents: upcoming,
    properties: (results.properties || []) as Property[],
    apartments: (results.apartments || []) as Apartment[]
  };
};

export const getHistoryPageData = async (organizationId: string | null): Promise<{
  activities: ActivityLog[];
  users: User[];
  apartments: Apartment[];
  properties: Property[];
}> => {
  const queries = [
    { collection: 'activity_log', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'apartments', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'properties', filter: {}, limit: 500 }
  ];
  
  const results = await batchQuery(queries);
  
  const activities = ((results.activity_log || []) as ActivityLog[]).sort((a, b) => 
    new Date(b.timestamp || '').getTime() - new Date(a.timestamp || '').getTime()
  );
  
  // Get users from CommHub cache
  const usersResponse = await getUsers({ organization_id: organizationId || undefined });
  
  return {
    activities,
    users: usersResponse.data || [],
    apartments: (results.apartments || []) as Apartment[],
    properties: (results.properties || []) as Property[]
  };
};

export const getEconomyPageData = async (organizationId: string | null): Promise<{
  incomes: IncomeRecord[];
  expenses: ExpenseRecord[];
  apartments: Apartment[];
  properties: Property[];
}> => {
  const queries = [
    { collection: 'income_records', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'expense_records', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'apartments', filter: organizationId ? { organization_id: organizationId } : {}, limit: 500 },
    { collection: 'properties', filter: {}, limit: 100 }
  ];
  
  const results = await batchQuery(queries);
  
  return {
    incomes: (results.income_records || []) as IncomeRecord[],
    expenses: (results.expense_records || []) as ExpenseRecord[],
    apartments: (results.apartments || []) as Apartment[],
    properties: (results.properties || []) as Property[]
  };
};

// ============ EXPORT FUNCTIONS ============

export const exportIssuesToExcel = async (issues: Issue[]): Promise<{ success: boolean }> => {
  const headers = ['ID', 'Titel', 'Status', 'Kategori', 'Prioritet', 'Skapad', 'Uppdaterad', 'Beskrivning'];
  const statusLabels: Record<string, string> = { open: 'Öppen', in_progress: 'Pågår', resolved: 'Löst', closed: 'Stängd' };
  const priorityLabels: Record<number, string> = { 1: 'Låg', 2: 'Normal', 3: 'Medel', 4: 'Hög', 5: 'Akut' };
  
  const rows = issues.map(issue => [
    issue.id,
    `"${(issue.title || '').replace(/"/g, '""')}"`,
    statusLabels[issue.status || ''] || issue.status,
    issue.category || '',
    priorityLabels[issue.urgency_level || 1] || String(issue.urgency_level),
    issue.created_at ? new Date(issue.created_at).toLocaleDateString('sv-SE') : '',
    issue.updated_at ? new Date(issue.updated_at).toLocaleDateString('sv-SE') : '',
    `"${(issue.description || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`
  ]);
  
  const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `arenden_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  return { success: true };
};

export const exportMaintenanceToExcel = async (schedules: MaintenanceSchedule[], options: { properties?: Property[] } = {}): Promise<{ success: boolean }> => {
  const { properties = [] } = options;
  
  const frequencyLabels: Record<string, string> = { daily: 'Dagligen', weekly: 'Veckovis', monthly: 'Månadsvis', quarterly: 'Kvartalsvis', yearly: 'Årligen', once: 'Engångs' };
  
  const headers = ['Titel', 'Kategori', 'Frekvens', 'Fastighet', 'Nästa datum', 'Senast utfört', 'Ansvarig'];
  
  const rows = schedules.map(s => [
    `"${(s.title || '').replace(/"/g, '""')}"`,
    s.category || '',
    frequencyLabels[s.frequency || ''] || s.frequency,
    properties.find(p => p.id === s.property_id)?.name || '',
    s.next_date ? new Date(s.next_date).toLocaleDateString('sv-SE') : '',
    s.last_completed ? new Date(s.last_completed).toLocaleDateString('sv-SE') : '',
    s.responsible || ''
  ]);
  
  const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `underhall_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  return { success: true };
};

export const exportProtocolToPDF = async (protocol: InspectionProtocol, options: { properties?: Property[]; apartments?: Apartment[] } = {}): Promise<{ success: boolean }> => {
  const { properties = [], apartments = [] } = options;
  
  const statusLabels: Record<string, string> = { ok: 'OK', minor_defect: 'Mindre brist', action_required: 'Åtgärd krävs', urgent: 'Akut' };
  const typeLabels: Record<string, string> = {
    annual: 'Årskontroll',
    move_in: 'Inflyttningsbesiktning',
    move_out: 'Utflyttningsbesiktning',
    damage: 'Skadekontroll',
    moisture: 'Fuktkontroll',
    safety: 'Säkerhetskontroll',
    other: 'Övrig kontroll'
  };
  
  const propertyName = properties.find(p => p.id === protocol.property_id)?.name || '';
  const apartmentNumber = apartments.find(a => a.id === protocol.apartment_id)?.apartment_number || '';
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Protokoll - ${typeLabels[protocol.type || ''] || protocol.type}</title>
      <style>
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
        h1 { color: #1e293b; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; }
        .info-row { display: flex; gap: 30px; margin-bottom: 20px; color: #64748b; }
        .section { margin: 20px 0; padding: 15px; border: 1px solid #e2e8f0; border-radius: 8px; }
        .section-header { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .section-title { font-weight: bold; font-size: 16px; }
        .status { padding: 4px 12px; border-radius: 20px; font-size: 12px; }
        .comment { margin-top: 10px; padding: 10px; background: #f8fafc; border-radius: 4px; font-size: 14px; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #94a3b8; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <h1>${typeLabels[protocol.type || ''] || protocol.type}</h1>
      
      <div class="info-row">
        <div>📅 ${protocol.inspection_date ? new Date(protocol.inspection_date).toLocaleDateString('sv-SE') : '-'}</div>
        ${propertyName ? `<div>🏢 ${propertyName}</div>` : ''}
        ${apartmentNumber ? `<div>🏠 Lgh ${apartmentNumber}</div>` : ''}
        ${protocol.inspector_name ? `<div>👤 ${protocol.inspector_name}</div>` : ''}
      </div>
      
      ${(protocol.items || []).map(item => `
        <div class="section">
          <div class="section-header">
            <span class="section-title">${item.building_part_name || item.building_part}</span>
            <span class="status">${statusLabels[item.status || ''] || item.status}</span>
          </div>
          ${item.comment ? `<div class="comment"><strong>Kommentar:</strong> ${item.comment}</div>` : ''}
          ${item.recommended_action ? `<div class="comment"><strong>Rekommenderad åtgärd:</strong> ${item.recommended_action}</div>` : ''}
        </div>
      `).join('')}
      
      <div class="footer">
        <p>Genererat: ${new Date().toLocaleString('sv-SE')}</p>
        <p>Status: ${protocol.status === 'completed' ? 'Slutförd' : 'Utkast'}</p>
      </div>
    </body>
    </html>
  `;
  
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
  
  return { success: true };
};

// Send resolution email
export const sendIssueResolvedEmail = async (issue: Issue, recipientEmail: string, recipientName: string, organizationName?: string): Promise<unknown> => {
  const subject = `Ditt ärende är löst: ${issue.title}`;
  const htmlContent = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: #22c55e; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
        <h1 style="margin: 0; font-size: 20px;">✓ Ärende löst</h1>
      </div>
      <div style="background: #f8fafc; padding: 20px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
        <p style="margin: 0 0 10px; color: #64748b;">Hej${recipientName ? ` ${recipientName}` : ''},</p>
        <p style="margin: 0 0 15px; color: #334155;">
          Vi vill meddela att ditt ärende nu har markerats som löst.
        </p>
        <div style="background: white; padding: 15px; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 15px;">
          <h3 style="margin: 0 0 10px; color: #1e293b;">${issue.title}</h3>
          <p style="margin: 0; color: #64748b; font-size: 14px;">${issue.description || ''}</p>
        </div>
        ${issue.resolution_notes ? `
          <div style="background: #f0fdf4; padding: 15px; border-radius: 6px; border: 1px solid #bbf7d0; margin-bottom: 15px;">
            <strong style="color: #166534;">Lösning:</strong>
            <p style="margin: 5px 0 0; color: #15803d;">${issue.resolution_notes}</p>
          </div>
        ` : ''}
      </div>
    </div>
  `;
  
  return await sendEmail(recipientEmail, subject, htmlContent, { fromName: organizationName || 'Förvaltningen' });
};

// Send supplier assignment email
export const sendSupplierAssignmentEmail = async (issue: Issue, supplier: Supplier, instructions: string = '', organizationName?: string): Promise<unknown> => {
  const subject = `Nytt uppdrag: ${issue.title}`;
  const htmlContent = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: #1e293b; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
        <h1 style="margin: 0; font-size: 20px;">Nytt uppdrag tilldelat</h1>
      </div>
      <div style="background: #f8fafc; padding: 20px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
        <p style="margin: 0 0 10px; color: #64748b;">Hej${supplier.name ? ` ${supplier.name}` : ''},</p>
        <p style="margin: 0 0 15px; color: #334155;">
          Ett nytt uppdrag har tilldelats er.
        </p>
        <div style="background: white; padding: 15px; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 15px;">
          <h3 style="margin: 0 0 10px; color: #1e293b;">${issue.title}</h3>
          <p style="margin: 0 0 10px; color: #64748b; font-size: 14px;">${issue.description || ''}</p>
          ${issue.category ? `<span style="display: inline-block; padding: 4px 12px; background: #e2e8f0; color: #475569; border-radius: 20px; font-size: 12px;">${issue.category}</span>` : ''}
        </div>
        ${instructions ? `
          <div style="background: #fffbeb; padding: 15px; border-radius: 6px; border: 1px solid #fde68a; margin-bottom: 15px;">
            <strong style="color: #92400e;">Instruktioner:</strong>
            <p style="margin: 5px 0 0; color: #a16207; white-space: pre-wrap;">${instructions}</p>
          </div>
        ` : ''}
      </div>
    </div>
  `;
  
  if (!supplier.email) {
    throw new Error('Leverantören saknar e-postadress');
  }
  
  return await sendEmail(supplier.email, subject, htmlContent, { fromName: organizationName || 'Förvaltningen' });
};

// ============ COMMHUB NATIVE USERS API ============
// These functions use CommHub's built-in user management system
// for migration from forvaltaren_users collection

export interface CommHubUser {
  id: string;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role?: string;
  status?: 'active' | 'inactive' | 'pending' | 'suspended';
  organization_id?: string;
  apartment_id?: string;
  property_id?: string;
  tags?: string[];
  groups?: string[];
  is_active?: boolean;
  is_invoice_recipient?: boolean;
  move_in_date?: string;
  move_out_date?: string;
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
  app_memberships?: { app_id: string; role: string }[];
  // Sensitive fields (only returned with special permission)
  personal_number?: string;
}

export interface CommHubUserCreate {
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role?: string;
  organization_id?: string;
  apartment_id?: string;
  property_id?: string;
  tags?: string[];
  groups?: string[];
  is_active?: boolean;
  is_invoice_recipient?: boolean;
  move_in_date?: string;
  app_id?: string;  // Required for app_memberships to be created
}

export interface CommHubUserUpdate {
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  role?: string;
  organization_id?: string | null;
  apartment_id?: string | null;
  property_id?: string | null;
  address?: {
    street?: string;
    postal_code?: string;
    city?: string;
    country?: string;
  };
  tags?: string[];
  groups?: string[];
  is_active?: boolean;
  is_invoice_recipient?: boolean;
  move_in_date?: string;
  move_out_date?: string;
  app_memberships?: { app_id: string; role: string }[];
}

// Helper to convert CommHubUser to User
const commHubUserToUser = (u: CommHubUser): User => {
  // Build full name from first_name and last_name
  const firstName = u.first_name || '';
  const lastName = u.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || u.name || '';
  
  return {
    id: u.id,
    name: fullName,
    email: u.email,
    phone: u.phone,
    role: (u.role || 'resident') as User['role'],
    organization_id: u.organization_id,
    apartment_id: u.apartment_id,
    property_id: u.property_id,
    is_active: u.is_active !== false,
    is_invoice_recipient: u.is_invoice_recipient || false,
    move_in_date: u.move_in_date,
    move_out_date: u.move_out_date
  };
};

export interface UserSyncConfig {
  collection_name: string;
  email_field: string;
  name_field?: string;
  phone_field?: string;
  role_field?: string;
  auto_create?: boolean;
  auto_update?: boolean;
  field_mapping?: Record<string, string>;
}

export interface UserSyncResult {
  total_processed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ email: string; error: string }>;
}

export interface ApartmentHistoryEntry {
  apartment_id: string;
  apartment_number?: string;
  property_name?: string;
  move_in_date: string;
  move_out_date?: string;
  is_current: boolean;
}

export interface UserAuditEntry {
  id: string;
  action: string;
  changed_fields?: Record<string, { old: unknown; new: unknown }>;
  performed_by?: string;
  performed_by_name?: string;
  timestamp: string;
  ip_address?: string;
}

// Get users from CommHub's native user system
export const getCommHubUsers = async (params: {
  organization_id?: string;
  role?: string;
  status?: string;
  apartment_id?: string;
  tags?: string[];
  limit?: number;
  offset?: number;
  search?: string;
} = {}): Promise<CommHubUser[]> => {
  // Use cached data and filter locally for better performance
  const cachedUsers = await getCommHubUsersCached();
  
  // Filter based on params
  let filteredUsers = cachedUsers as unknown as CommHubUser[];
  
  if (params.organization_id) {
    filteredUsers = filteredUsers.filter(u => u.organization_id === params.organization_id);
  }
  if (params.role) {
    filteredUsers = filteredUsers.filter(u => u.role === params.role);
  }
  if (params.status) {
    filteredUsers = filteredUsers.filter(u => u.status === params.status);
  }
  if (params.apartment_id) {
    filteredUsers = filteredUsers.filter(u => u.apartment_id === params.apartment_id);
  }
  if (params.search) {
    const searchLower = params.search.toLowerCase();
    filteredUsers = filteredUsers.filter(u => 
      u.email?.toLowerCase().includes(searchLower) ||
      u.first_name?.toLowerCase().includes(searchLower) ||
      u.last_name?.toLowerCase().includes(searchLower) ||
      u.name?.toLowerCase().includes(searchLower)
    );
  }
  if (params.tags?.length) {
    filteredUsers = filteredUsers.filter(u => 
      params.tags?.some(tag => u.tags?.includes(tag))
    );
  }
  
  // Apply offset and limit
  const offset = params.offset || 0;
  const limit = params.limit || filteredUsers.length;
  filteredUsers = filteredUsers.slice(offset, offset + limit);
  
  return filteredUsers;
};

// Get single user from CommHub (uses cache)
export const getCommHubUser = async (userId: string): Promise<CommHubUser | null> => {
  // Try cache first
  const cachedUsers = await getCommHubUsersCached();
  const cachedUser = cachedUsers.find(u => u.id === userId);
  
  if (cachedUser) {
    return cachedUser as unknown as CommHubUser;
  }
  
  // If not in cache, fetch directly
  try {
    const response = await commhub.get(`/users/${userId}`);
    return response.data;
  } catch {
    return null;
  }
};

// Create user in CommHub's native system
export const createCommHubUser = async (userData: CommHubUserCreate): Promise<ApiResponse<CommHubUser>> => {
  try {
    // Always include app_id so CommHub creates app_memberships
    const dataWithAppId = {
      ...userData,
      app_id: userData.app_id || COMMHUB_APP_ID
    };
    const response = await commhub.post('/users', dataWithAppId);
    
    const createdUser = response.data as CommHubUser;
    
    // CommHub doesn't automatically create app_memberships from app_id
    // We need to explicitly set it via a PUT request
    if (createdUser && createdUser.id) {
      const appMemberships = createdUser.app_memberships || [];
      const hasAppMembership = appMemberships.some(
        (m: { app_id: string }) => m.app_id === COMMHUB_APP_ID
      );
      
      if (!hasAppMembership) {
        try {
          const updateResponse = await commhub.put(`/users/${createdUser.id}`, {
            app_memberships: [{ app_id: COMMHUB_APP_ID, role: 'user' }]
          });
          return updateResponse.data;
        } catch (updateError) {
          console.warn('Could not set app_memberships after create:', updateError);
        }
      }
    }
    
    return response.data;
  } catch (error: unknown) {
    console.error('Failed to create CommHub user:', error);
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage = axiosError.response?.data?.message || axiosError.response?.data?.error || 'Kunde inte skapa användare';
      throw new Error(errorMessage);
    }
    throw error;
  }
};

// Update user in CommHub
export const updateCommHubUser = async (userId: string, userData: CommHubUserUpdate): Promise<ApiResponse<CommHubUser>> => {
  console.log('[updateCommHubUser] Updating user:', userId, 'with data:', JSON.stringify(userData));
  const response = await commhub.put(`/users/${userId}`, userData);
  console.log('[updateCommHubUser] Response:', JSON.stringify(response.data));
  return response.data;
};

// Delete user from CommHub
export const deleteCommHubUser = async (userId: string): Promise<ApiResponse<{ success: boolean }>> => {
  const response = await commhub.delete(`/users/${userId}`);
  return response.data;
};

// Get user's sensitive data (requires elevated permissions)
export const getCommHubUserSensitiveData = async (userId: string): Promise<ApiResponse<{ personal_number?: string }>> => {
  const response = await commhub.get(`/users/${userId}/sensitive`);
  return response.data;
};

// Update user's sensitive data
export const updateCommHubUserSensitiveData = async (userId: string, data: { personal_number?: string }): Promise<ApiResponse<{ success: boolean }>> => {
  const response = await commhub.put(`/users/${userId}/sensitive`, data);
  return response.data;
};

// Get user's apartment history
export const getCommHubUserApartmentHistory = async (userId: string): Promise<ApiResponse<ApartmentHistoryEntry[]>> => {
  const response = await commhub.get(`/users/${userId}/apartment-history`);
  return response.data;
};

// Move user to new apartment
export const moveCommHubUserToApartment = async (userId: string, data: {
  apartment_id: string;
  move_in_date?: string;
  notes?: string;
}): Promise<ApiResponse<CommHubUser>> => {
  const response = await commhub.post(`/users/${userId}/move`, data);
  return response.data;
};

// Move user out of apartment
export const moveCommHubUserOut = async (userId: string, data: {
  move_out_date?: string;
  notes?: string;
}): Promise<ApiResponse<CommHubUser>> => {
  const response = await commhub.post(`/users/${userId}/move-out`, data);
  return response.data;
};

// Get user's audit log
export const getCommHubUserAuditLog = async (userId: string, params: {
  limit?: number;
  offset?: number;
} = {}): Promise<ApiResponse<UserAuditEntry[]>> => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.offset) queryParams.append('offset', params.offset.toString());
  
  const response = await commhub.get(`/users/${userId}/audit-log?${queryParams.toString()}`);
  return response.data;
};

// Get user's timeline
export const getCommHubUserTimeline = async (userId: string): Promise<ApiResponse<UserAuditEntry[]>> => {
  const response = await commhub.get(`/users/${userId}/timeline`);
  return response.data;
};

// Bulk import users
export const bulkImportCommHubUsers = async (users: CommHubUserCreate[]): Promise<ApiResponse<{
  total: number;
  created: number;
  updated: number;
  errors: Array<{ index: number; email: string; error: string }>;
}>> => {
  const response = await commhub.post('/users/bulk-import', users);
  return response.data;
};

// Sync users from collection (forvaltaren_users -> CommHub Users)
export const syncUsersFromCollection = async (config: UserSyncConfig): Promise<ApiResponse<UserSyncResult>> => {
  const response = await commhub.post(`/public/${COMMHUB_APP_ID}/sync-users`, config);
  return response.data;
};

// Get sync status
export const getUserSyncStatus = async (): Promise<ApiResponse<{
  last_sync?: string;
  total_synced?: number;
  status: 'idle' | 'running' | 'completed' | 'failed';
  error?: string;
}>> => {
  const response = await commhub.get(`/public/${COMMHUB_APP_ID}/sync-status`);
  return response.data;
};

// Get available roles
export const getAvailableRoles = async (): Promise<ApiResponse<string[]>> => {
  const response = await commhub.get('/users/roles');
  return response.data;
};

// Get user stats
export const getCommHubUserStats = async (): Promise<ApiResponse<{
  total_users: number;
  active_users: number;
  by_role: Record<string, number>;
  by_status: Record<string, number>;
  recent_signups: number;
}>> => {
  const response = await commhub.get('/users/stats');
  return response.data;
};

// Create invitation
export const createUserInvitation = async (data: {
  email: string;
  role?: string;
  organization_id?: string;
  apartment_id?: string;
  message?: string;
}): Promise<ApiResponse<{ invitation_id: string; expires_at: string }>> => {
  const response = await commhub.post('/users/invitations', data);
  return response.data;
};

// List invitations
export const listUserInvitations = async (): Promise<ApiResponse<Array<{
  id: string;
  email: string;
  role?: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  created_at: string;
  expires_at: string;
}>>> => {
  const response = await commhub.get('/users/invitations');
  return response.data;
};

// ============ MIGRATION HELPERS ============

// Migrate a single user from forvaltaren_users to CommHub Users
export const migrateUserToCommHub = async (legacyUser: User): Promise<CommHubUser> => {
  // Create user in CommHub
  const response = await createCommHubUser({
    email: legacyUser.email || '',
    name: legacyUser.name,
    phone: legacyUser.phone,
    role: legacyUser.role,
    organization_id: legacyUser.organization_id,
    apartment_id: legacyUser.apartment_id,
    property_id: legacyUser.property_id,
    is_active: legacyUser.is_active !== false,
    move_in_date: legacyUser.move_in_date,
  });
  
  // CommHub API returns the user directly or wrapped - handle both cases
  const commHubUser: CommHubUser = (response as { id?: string }).id 
    ? response as unknown as CommHubUser 
    : response as unknown as CommHubUser;
  
  // If user has personal_number, store it in sensitive data
  if (legacyUser.personal_number && commHubUser?.id) {
    try {
      await updateCommHubUserSensitiveData(commHubUser.id, {
        personal_number: legacyUser.personal_number
      });
    } catch (error) {
      console.warn('Could not migrate sensitive data for user:', legacyUser.email, error);
    }
  }
  
  return commHubUser;
};

// Batch migrate all users from forvaltaren_users
export const migrateAllUsersToCommHub = async (options: {
  dryRun?: boolean;
  onProgress?: (current: number, total: number, user: User) => void;
} = {}): Promise<{
  total: number;
  migrated: number;
  skipped: number;
  errors: Array<{ email: string; error: string }>;
}> => {
  const { dryRun = false, onProgress } = options;
  
  // Get all users from legacy collection
  const legacyUsers = await queryCollection<User>('forvaltaren_users');
  
  const result = {
    total: legacyUsers.length,
    migrated: 0,
    skipped: 0,
    errors: [] as Array<{ email: string; error: string }>
  };
  
  for (let i = 0; i < legacyUsers.length; i++) {
    const user = legacyUsers[i];
    onProgress?.(i + 1, legacyUsers.length, user);
    
    if (!user.email) {
      result.errors.push({ email: user.id, error: 'Missing email' });
      result.skipped++;
      continue;
    }
    
    if (dryRun) {
      console.log(`[DRY RUN] Would migrate user: ${user.email}`);
      result.migrated++;
      continue;
    }
    
    try {
      await migrateUserToCommHub(user);
      result.migrated++;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      result.errors.push({ email: user.email, error: errorMessage });
    }
  }
  
  return result;
};


// Batch update organization_id for all CommHub users
export const batchUpdateUsersOrganizationId = async (organizationId: string): Promise<{ updated: number; failed: number }> => {
  // Get all CommHub users - getCommHubUsers returns ApiResponse<CommHubUser[]>
  const response = await getCommHubUsers({ limit: 500 });
  
  // Handle both possible response formats
  let users: CommHubUser[] = [];
  if (Array.isArray(response)) {
    users = response;
  } else if (response && typeof response === 'object' && 'data' in response) {
    users = (response as { data: CommHubUser[] }).data || [];
  }
  
  let updated = 0;
  let failed = 0;
  
  // Update each user that doesn't have the correct organization_id
  for (const user of users) {
    if (user.organization_id !== organizationId) {
      try {
        await updateCommHubUser(user.id, { organization_id: organizationId });
        updated++;
      } catch (error) {
        console.error(`Failed to update user ${user.id}:`, error);
        failed++;
      }
    }
  }
  
  return { updated, failed };
};


// Batch update apartment_id to apartment_number for all CommHub users
export const batchUpdateUsersApartmentNumber = async (): Promise<{ updated: number; failed: number; skipped: number }> => {
  // Step 1: Get all apartments to create ID -> Number mapping
  const apartments = await queryCollection<Apartment>('apartments');
  const apartmentMap: Record<string, string> = {};
  
  apartments.forEach(apt => {
    if (apt.id && apt.apartment_number) {
      apartmentMap[apt.id] = apt.apartment_number;
    }
  });
  
  console.log('Apartment mapping:', apartmentMap);
  
  // Step 2: Get all CommHub users
  const response = await getCommHubUsers({ limit: 500 });
  let users: CommHubUser[] = [];
  
  if (Array.isArray(response)) {
    users = response;
  } else if (response && typeof response === 'object' && 'data' in response) {
    users = (response as { data: CommHubUser[] }).data || [];
  }
  
  let updated = 0;
  let failed = 0;
  let skipped = 0;
  
  // Step 3: Update each user's apartment_id to the apartment number
  for (const user of users) {
    const currentApartmentId = user.apartment_id;
    
    // Skip if no apartment_id
    if (!currentApartmentId) {
      skipped++;
      continue;
    }
    
    // Check if it's a UUID (internal ID) that needs to be converted
    const isUUID = currentApartmentId.includes('-') && currentApartmentId.length > 10;
    
    if (isUUID && apartmentMap[currentApartmentId]) {
      // Convert UUID to apartment number
      const apartmentNumber = apartmentMap[currentApartmentId];
      try {
        await updateCommHubUser(user.id, { apartment_id: apartmentNumber });
        console.log(`Updated ${user.email}: ${currentApartmentId} -> ${apartmentNumber}`);
        updated++;
      } catch (error) {
        console.error(`Failed to update user ${user.id}:`, error);
        failed++;
      }
    } else {
      // Already has apartment number or not in mapping
      skipped++;
    }
  }
  
  return { updated, failed, skipped };
};

// Batch update address for all CommHub users based on apartment -> property -> address
export const batchUpdateUsersAddress = async (): Promise<{ updated: number; failed: number; skipped: number }> => {
  // Step 1: Get all apartments to create apartment_id/number -> (property_id, apartment_number) mapping
  const apartments = await queryCollection<Apartment>('apartments');
  const apartmentData: Record<string, { propertyId: string; apartmentNumber: string }> = {};
  
  apartments.forEach(apt => {
    const aptNum = apt.apartment_number || '';
    const propId = apt.property_id || '';
    
    if (apt.id && propId) {
      apartmentData[apt.id] = { propertyId: propId, apartmentNumber: aptNum };
    }
    // Also map by apartment_number for users who have number instead of ID
    if (aptNum && propId) {
      apartmentData[aptNum] = { propertyId: propId, apartmentNumber: aptNum };
    }
  });
  
  console.log('Apartment data mapping:', apartmentData);
  
  // Step 2: Get all properties to create property_id -> address mapping
  const properties = await queryCollection<Property>('properties');
  const propertyData: Record<string, { address: string; name: string }> = {};
  
  properties.forEach(prop => {
    if (prop.id) {
      propertyData[prop.id] = { 
        address: prop.address || '', 
        name: prop.name || '' 
      };
    }
  });
  
  console.log('Property data mapping:', propertyData);
  
  // Step 3: Get all CommHub users
  const response = await getCommHubUsers({ limit: 500 });
  let users: CommHubUser[] = [];
  
  if (Array.isArray(response)) {
    users = response;
  } else if (response && typeof response === 'object' && 'data' in response) {
    users = (response as { data: CommHubUser[] }).data || [];
  }
  
  let updated = 0;
  let failed = 0;
  let skipped = 0;
  
  // Step 4: Update each user with full address object
  for (const user of users) {
    const apartmentId = user.apartment_id;
    
    // Skip if no apartment_id
    if (!apartmentId) {
      console.log(`Skipping ${user.email}: No apartment_id`);
      skipped++;
      continue;
    }
    
    // Find apartment data
    const aptData = apartmentData[apartmentId];
    if (!aptData) {
      console.log(`Skipping ${user.email}: No apartment data for ${apartmentId}`);
      skipped++;
      continue;
    }
    
    // Find property data
    const propData = propertyData[aptData.propertyId];
    if (!propData) {
      console.log(`Skipping ${user.email}: No property data for ${aptData.propertyId}`);
      skipped++;
      continue;
    }
    
    // Build full street address: "Grindstugatan 5A" 
    const streetAddress = `${propData.address} ${aptData.apartmentNumber}`.trim();
    
    // Build address object for CommHub
    const addressObj = {
      street: streetAddress,
      postal_code: "13431",
      city: "Gustavsberg",
      country: "Sverige"
    };
    
    // Update user with address object and property_id
    try {
      await updateCommHubUser(user.id, { 
        address: addressObj,
        property_id: aptData.propertyId
      });
      console.log(`Updated ${user.email}: address = "${streetAddress}"`);
      updated++;
    } catch (error) {
      console.error(`Failed to update user ${user.id}:`, error);
      failed++;
    }
  }
  
  return { updated, failed, skipped };
};


// ============================================
// PORTAL & ANNOUNCEMENTS API
// ============================================

// Predefined document categories
export const DOCUMENT_CATEGORIES = [
  { id: 'annual_report', label: 'Årsredovisning' },
  { id: 'protocol', label: 'Protokoll' },
  { id: 'statutes', label: 'Stadgar' },
  { id: 'rules', label: 'Ordningsregler' },
  { id: 'maintenance_plan', label: 'Underhållsplan' },
  { id: 'budget', label: 'Budget' },
  { id: 'insurance', label: 'Försäkring' },
  { id: 'contract', label: 'Avtal' },
  { id: 'manual', label: 'Manual/Instruktion' },
  { id: 'other', label: 'Övrigt' },
];

// Get organization by slug (for public portal)
export const getOrganizationBySlug = async (slug: string): Promise<Organization | null> => {
  try {
    const orgs = await queryCollection<Organization>('organizations', { slug });
    return orgs.length > 0 ? orgs[0] : null;
  } catch (error) {
    console.error('Failed to get organization by slug:', error);
    return null;
  }
};

// Generate a unique 8-character subdomain code
export const generateSubdomainCode = (): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Get organization by subdomain code (for wildcard subdomain routing)
export const getOrganizationBySubdomainCode = async (code: string): Promise<Organization | null> => {
  try {
    const orgs = await queryCollection<Organization>('organizations', { subdomain_code: code });
    return orgs.length > 0 ? orgs[0] : null;
  } catch (error) {
    console.error('Failed to get organization by subdomain code:', error);
    return null;
  }
};

// Get organization by custom domain (CNAME lookup)
export const getOrganizationByCustomDomain = async (domain: string): Promise<Organization | null> => {
  try {
    // Query organizations where portal_settings.custom_domain matches
    const allOrgs = await queryCollection<Organization>('organizations', {});
    const matchingOrg = allOrgs.find(org => 
      org.portal_settings?.custom_domain?.toLowerCase() === domain.toLowerCase()
    );
    return matchingOrg || null;
  } catch (error) {
    console.error('Failed to get organization by custom domain:', error);
    return null;
  }
};

// Generate or regenerate subdomain code for an organization
export const generateOrganizationSubdomainCode = async (organizationId: string): Promise<string | null> => {
  try {
    // Generate a unique code
    let code = generateSubdomainCode();
    let attempts = 0;
    
    // Make sure code is unique
    while (attempts < 10) {
      const existing = await getOrganizationBySubdomainCode(code);
      if (!existing) break;
      code = generateSubdomainCode();
      attempts++;
    }
    
    // Update organization with new code
    await updateDocument('organizations', organizationId, { subdomain_code: code });
    
    return code;
  } catch (error) {
    console.error('Failed to generate subdomain code:', error);
    return null;
  }
};

// Generate subdomain codes for all organizations that don't have one
export const generateMissingSubdomainCodes = async (): Promise<number> => {
  try {
    const allOrgs = await queryCollection<Organization>('organizations', {});
    let generated = 0;
    
    for (const org of allOrgs) {
      if (!org.subdomain_code && org.id) {
        await generateOrganizationSubdomainCode(org.id);
        generated++;
      }
    }
    
    return generated;
  } catch (error) {
    console.error('Failed to generate missing subdomain codes:', error);
    return 0;
  }
};

// Get portal data (public or authenticated)
export const getPortalData = async (organizationId: string, isAuthenticated: boolean = false): Promise<{
  organization: Organization | null;
  announcements: Announcement[];
  documents: Document[];
  calendarEvents: CalendarEvent[];
  galleryImages: GalleryImage[];
}> => {
  try {
    // Get organization
    const orgs = await queryCollection<Organization>('organizations', { id: organizationId });
    const organization = orgs.length > 0 ? orgs[0] : null;
    
    // Get announcements
    const allAnnouncements = await queryCollection<Announcement>('announcements', { 
      organization_id: organizationId,
      is_published: true 
    });
    
    // Filter based on auth status and end_date (expired announcements are hidden)
    const now = new Date();
    const announcements = (isAuthenticated 
      ? allAnnouncements 
      : allAnnouncements.filter(a => a.is_public !== false))
      .filter(a => !a.end_date || new Date(a.end_date) >= now);
    
    // Get documents
    const allDocuments = await queryCollection<Document>('documents', { 
      organization_id: organizationId 
    });
    
    // Filter based on auth status and visibility
    const documents = isAuthenticated
      ? allDocuments
      : allDocuments.filter(d => d.visibility === 'public');
    
    // Get calendar events
    const allCalendarEvents = await queryCollection<CalendarEvent>('calendar_events', {
      organization_id: organizationId
    });
    
    // Filter to only public events and future events
    const calendarEvents = (isAuthenticated ? allCalendarEvents : allCalendarEvents.filter(e => e.is_public !== false))
      .filter(e => new Date(e.start_date || '') >= new Date())
      .sort((a, b) => new Date(a.start_date || '').getTime() - new Date(b.start_date || '').getTime());
    
    // Get gallery images
    const allGalleryImages = await queryCollection<GalleryImage>('gallery_images', {
      organization_id: organizationId
    });
    
    // Filter to only public images and sort by sort_order
    const galleryImages = (isAuthenticated ? allGalleryImages : allGalleryImages.filter(img => img.is_public !== false))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    
    return { organization, announcements, documents, calendarEvents, galleryImages };
  } catch (error) {
    console.error('Failed to get portal data:', error);
    return { organization: null, announcements: [], documents: [], calendarEvents: [], galleryImages: [] };
  }
};

// Create announcement
export const createAnnouncement = async (data: Partial<Announcement>): Promise<Announcement> => {
  return await createDocument('announcements', {
    ...data,
    id: data.id || crypto.randomUUID(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    is_published: data.is_published ?? true,
    is_public: data.is_public ?? false
  });
};

// Update announcement
export const updateAnnouncement = async (id: string, data: Partial<Announcement>): Promise<Announcement> => {
  return await updateDocument<Announcement>('announcements', id, {
    ...data,
    updated_at: new Date().toISOString()
  });
};

// Delete announcement
export const deleteAnnouncement = async (id: string): Promise<void> => {
  await deleteDocument('announcements', id);
};

// Get announcements for organization
export const getAnnouncements = async (organizationId: string): Promise<Announcement[]> => {
  return await queryCollection<Announcement>('announcements', { organization_id: organizationId });
};

// Update organization portal settings
export const updatePortalSettings = async (organizationId: string, settings: Organization['portal_settings']): Promise<Organization> => {
  return await updateDocument<Organization>('organizations', organizationId, {
    portal_settings: settings,
    updated_at: new Date().toISOString()
  });
};

// Update organization slug
export const updateOrganizationSlug = async (organizationId: string, slug: string): Promise<Organization> => {
  // Check if slug is already taken
  const existing = await queryCollection<Organization>('organizations', { slug });
  if (existing.length > 0 && existing[0].id !== organizationId) {
    throw new Error('Denna URL är redan upptagen');
  }
  
  return await updateDocument<Organization>('organizations', organizationId, {
    slug,
    updated_at: new Date().toISOString()
  });
};

// Get all unique document categories (predefined + custom)
export const getDocumentCategories = async (organizationId: string): Promise<string[]> => {
  const docs = await queryCollection<Document>('documents', { organization_id: organizationId });
  const customCategories = new Set<string>();
  
  docs.forEach(doc => {
    if (doc.category && !DOCUMENT_CATEGORIES.find(c => c.id === doc.category)) {
      customCategories.add(doc.category);
    }
    if (doc.custom_category) {
      customCategories.add(doc.custom_category);
    }
  });
  
  return Array.from(customCategories);
};


// ============================================
// Calendar Events CRUD
// ============================================

// Create calendar event
export const createCalendarEvent = async (data: Partial<CalendarEvent>): Promise<CalendarEvent> => {
  return await createDocument('calendar_events', {
    ...data,
    id: data.id || crypto.randomUUID(),
    is_public: data.is_public !== false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
};

// Update calendar event
export const updateCalendarEvent = async (id: string, data: Partial<CalendarEvent>): Promise<CalendarEvent> => {
  return await updateDocument<CalendarEvent>('calendar_events', id, {
    ...data,
    updated_at: new Date().toISOString()
  });
};

// Delete calendar event
export const deleteCalendarEvent = async (id: string): Promise<void> => {
  await deleteDocument('calendar_events', id);
};

// Get calendar events for organization
export const getCalendarEvents = async (organizationId: string): Promise<CalendarEvent[]> => {
  const events = await queryCollection<CalendarEvent>('calendar_events', { organization_id: organizationId });
  return events.sort((a, b) => new Date(a.start_date || '').getTime() - new Date(b.start_date || '').getTime());
};

// ============================================
// Gallery Images CRUD
// ============================================

// Create gallery image
export const createGalleryImage = async (data: Partial<GalleryImage>): Promise<GalleryImage> => {
  return await createDocument('gallery_images', {
    ...data,
    id: data.id || crypto.randomUUID(),
    is_public: data.is_public !== false,
    sort_order: data.sort_order || 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
};

// Update gallery image
export const updateGalleryImage = async (id: string, data: Partial<GalleryImage>): Promise<GalleryImage> => {
  return await updateDocument<GalleryImage>('gallery_images', id, {
    ...data,
    updated_at: new Date().toISOString()
  });
};

// Delete gallery image
export const deleteGalleryImage = async (id: string): Promise<void> => {
  await deleteDocument('gallery_images', id);
};

// Get gallery images for organization
export const getGalleryImages = async (organizationId: string): Promise<GalleryImage[]> => {
  const images = await queryCollection<GalleryImage>('gallery_images', { organization_id: organizationId });
  return images.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

// Reorder gallery images
export const reorderGalleryImages = async (organizationId: string, imageIds: string[]): Promise<void> => {
  const updates = imageIds.map((id, index) => 
    updateGalleryImage(id, { sort_order: index })
  );
  await Promise.all(updates);
};


// Export commhub instance
export { commhub };
export default commhub;
